import React from "react";
import { Section } from "./intelligent-pricing-model-components/Section";
import { motion } from "motion/react";
import { Clock, ShoppingCart, Zap } from "lucide-react";
import { ContentCard } from "./intelligent-pricing-model-components/ContentCard";
import { SectionHeader } from "./intelligent-pricing-model-components/SectionHeader";
import { TextReveal } from "./intelligent-pricing-model-components/TextReveal";
import { ParallaxText } from "./intelligent-pricing-model-components/ParallaxText";
import { FindingCard } from "./intelligent-pricing-model-components/FindingCard";
import svgPaths from "./intelligent-pricing-model-components/svgPaths";
import { ResearchCard } from "./intelligent-pricing-model-components/ResearchCard";
import { InsightCard } from "./intelligent-pricing-model-components/InsightCard";
import { CompetitorRow } from "./intelligent-pricing-model-components/CompetitorRow";
import ContentSection, {
  Paragraph,
} from "./intelligent-pricing-model-components/ContentSection";
import BarChart from "./intelligent-pricing-model-components/BarChart";
import PieChart from "./intelligent-pricing-model-components/PieChart";
import ParallaxSection from "./intelligent-pricing-model-components/ParallaxSection";
import { AnimatedSection } from "./intelligent-pricing-model-components/AnimatedSection";
import { DemographicsTable } from "./intelligent-pricing-model-components/DemographicsTable";
import { ExperienceChart } from "./intelligent-pricing-model-components/ExperienceChart";
import ProgressBar from "./intelligent-pricing-model-components/ProgressBar";
import PieChartAdvanced from "./intelligent-pricing-model-components/PieChartAdvanced";
import TopInsights from "./intelligent-pricing-model-components/TopInsights";
import JourneyMap from "./intelligent-pricing-model-components/JourneyMap";
import { FeatureCard } from "./FeatureCard";
import { QuestionCard } from "./intelligent-pricing-model-components/QuestionCard";
import { DetailCard } from "./intelligent-pricing-model-components/DetailCard";
import { OutcomeCard } from "./intelligent-pricing-model-components/OutcomeCard";
import TKDID from "./intelligent-pricing-model-components/TLDID";
import GlassCard from "./intelligent-pricing-model-components/GlassCard";
import CoralCard from "./intelligent-pricing-model-components/CoralCard";

const tableOfContentsData = [
  // Column 1
  { text: "Executive Summary", level: 0 },
  { text: "Introduction", level: 0 },
  { text: "Background and Context", level: 0 },
  { text: "Project Objective", level: 0 },
  { text: "Scope of the Research", level: 0 },
  { text: "Secondary Research", level: 0 },
  { text: "Literature Review", level: 1 },
  { text: "Competitive Analysis", level: 2 },
  { text: "Machine Learning in Airbnb Pricing", level: 2 },
  { text: "Key Insights from Secondary Research", level: 2 },
  { text: "Conclusion of Secondary Research", level: 2 },
  { text: "Primary Research", level: 0 },
  { text: "Introduction", level: 2 },
  { text: "Participant Recruitment Strategy", level: 2 },
  { text: "Research Methods", level: 2 },
  { text: "Key Insights", level: 2 },
  { text: "Design Recommendations", level: 2 },
  // Column 2
  { text: "Ideation", level: 0 },
  { text: "Concept Development", level: 2 },
  { text: "Design Rationale", level: 2 },
  {
    text: "Methodology - Pricing Model Development and Data Sources",
    level: 0,
  },
  { text: "Design Process", level: 0 },
  { text: "Storyboarding", level: 2 },
  { text: "User Flow", level: 2 },
  { text: "Wireframing", level: 2 },
  { text: "Low Fidelity Mock-ups", level: 2 },
  { text: "High Fidelity Mock-ups", level: 2 },
  { text: "Usability Testing", level: 0 },
  { text: "Findings from Usability Testing", level: 2 },
  { text: "Key Learnings from Usability Testing", level: 2 },
  { text: "Iterative Design & Final Design", level: 0 },
  { text: "Final Solution - Intelligent Pricing Tool UI", level: 2 },
  { text: "Data Model Architecture", level: 2 },
  { text: "Final Prototype Demo", level: 2 },
  { text: "Business Model Canvas", level: 0 },
  { text: "Conclusion", level: 0 },
  { text: "Future Scope", level: 0 },
  { text: "References", level: 0 },
  { text: "Appendix", level: 0 },
  { text: "Appendix 1: Competitive Analysis", level: 2 },
  { text: "Appendix 2: Thematic Analysis", level: 2 },
];
const neighborhoodData = [
  { label: "Manhattan", value: 44, color: "#D4B896" },
  { label: "Brooklyn", value: 41, color: "#C5A583" },
  { label: "Queens", value: 12, color: "#B69270" },
  { label: "Bronx", value: 2, color: "#A77F5D" },
  { label: "Staten Island", value: 1, color: "#986C4A" },
];

const roomTypeData = [
  { label: "Private Room", value: 45.7, color: "#D4B896" },
  { label: "Entire home/apt", value: 51.9, color: "#F5D8B5" },
  { label: "Shared Room", value: 2.4, color: "#C5A583" },
];

const availabilityData = [
  { label: "Available", value: 64, color: "#F5D8B5" },
  { label: "Not Available", value: 36, color: "#B69270" },
];
function IntelligentPricingModelPage() {
  const column1Items = tableOfContentsData.slice(0, 17);
  const column2Items = tableOfContentsData.slice(17);
  return (
    <div className="bg-[#0d0d0d] min-h-screen w-full overflow-x-hidden text-white overflow-y-hidden">
      {/* hero Section */}
      <Section className="min-h-[60vh] md:min-h-screen flex items-center justify-center relative">
        <div className="absolute inset-0 opacity-20">
          <img
            src="/assets/imgs/case-studies/ipm_6.png"
            alt="Fresh vegetables"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0d0d0d] via-transparent to-[#0d0d0d]" />
        </div>

        <div className="relative z-10 text-center px-6 max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div
              className="inline-block mb-6 px-6 py-2 bg-[#fd5b38]/10 item-border border-[#fd5b38]/30 rounded-full"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              <span className="text-[#fd5b38]">
                HCID-699 Applied Project • April 2025
              </span>
            </motion.div>
            <motion.h1
              className="mb-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              <span className="block text-3xl md:text-5xl lg:text-6xl mb-4">
                Intelligent Pricing Model
              </span>
              <span className="block text-2xl md:text-3xl lg:text-4xl text-gray-400">
                for Airbnb Hosts
              </span>
            </motion.h1>
            <motion.p
              className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto mb-12"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.8 }}
            >
              A data-driven solution empowering Airbnb hosts to optimize rental
              pricing through intelligent analytics and market insights.
            </motion.p>
            <motion.div
              className="flex gap-4 justify-center flex-wrap"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.8 }}
            >
              <div className="flex items-center gap-2 text-gray-400">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_price)">
                    <path
                      d="M13.334 5.83203H18.334V10.832"
                      stroke="#FD5B38"
                      strokeWidth="1.66667"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M18.3327 5.83203L11.2493 12.9154L7.08268 8.7487L1.66602 14.1654"
                      stroke="#FD5B38"
                      strokeWidth="1.66667"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_price">
                      <rect width="20" height="20" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
                <span>Price Optimization</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2.5 2.5V15.8333C2.5 16.2754 2.67559 16.6993 2.98816 17.0118C3.30072 17.3244 3.72464 17.5 4.16667 17.5H17.5"
                    stroke="#FD5B38"
                    strokeWidth="1.66667"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M15 14.1667V7.5"
                    stroke="#FD5B38"
                    strokeWidth="1.66667"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M10.834 14.168V4.16797"
                    stroke="#FD5B38"
                    strokeWidth="1.66667"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M6.66602 14.168V11.668"
                    stroke="#FD5B38"
                    strokeWidth="1.66667"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Market Analytics</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_revenue)">
                    <path
                      d="M9.99935 18.3346C14.6017 18.3346 18.3327 14.6037 18.3327 10.0013C18.3327 5.39893 14.6017 1.66797 9.99935 1.66797C5.39698 1.66797 1.66602 5.39893 1.66602 10.0013C1.66602 14.6037 5.39698 18.3346 9.99935 18.3346Z"
                      stroke="#FD5B38"
                      strokeWidth="1.66667"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M10 15C12.7614 15 15 12.7614 15 10C15 7.23858 12.7614 5 10 5C7.23858 5 5 7.23858 5 10C5 12.7614 7.23858 15 10 15Z"
                      stroke="#FD5B38"
                      strokeWidth="1.66667"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M10.0007 11.6654C10.9211 11.6654 11.6673 10.9192 11.6673 9.9987C11.6673 9.07822 10.9211 8.33203 10.0007 8.33203C9.08018 8.33203 8.33398 9.07822 8.33398 9.9987C8.33398 10.9192 9.08018 11.6654 10.0007 11.6654Z"
                      stroke="#FD5B38"
                      strokeWidth="1.66667"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_revenue">
                      <rect width="20" height="20" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
                <span>Revenue Maximization</span>
              </div>
            </motion.div>
          </motion.div>
        </div>

        <motion.div
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="text-[#fd5b38]"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                d="M12 5v14M19 12l-7 7-7-7"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </motion.div>
        </motion.div>
      </Section>
      {/* Table of Contents */}
      <Section id="table-of-contents">
        <div className="max-w-7xl mx-auto flex flex-col gap-12 md:gap-16 lg:gap-20">
          <SectionHeader number="" title="Table of Contents" />
          <div
            aria-label="Document table of contents"
            className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8"
          >
            {/* Column 1 */}
            <ContentCard items={column1Items} startIndex={0} />

            {/* Column 2 */}
            <ContentCard
              items={column2Items}
              startIndex={column1Items.length}
            />
          </div>
        </div>
      </Section>
      {/* Executive Summary */}
      <Section id="executive-summary">
        <div className="max-w-7xl mx-auto">
          <SectionHeader number="" title="Executive Summary" />
          <div className="mt-6 md:mt-12 space-y-4">
            <ParallaxText offset={20}>
              <TextReveal delay={0.2}>
                <p className="text-[#d1d5dc]">
                  This project aims to build a machine learning-based pricing
                  tool to help Airbnb hosts set competitive and profitable
                  listing prices. The short-term rental market is highly
                  dynamic, and unlike hotels with structured pricing strategies,
                  most hosts rely on intuition or limited data. This often
                  results in overpricing or underpricing.
                </p>
              </TextReveal>
            </ParallaxText>
            <ParallaxText offset={15}>
              <TextReveal delay={0.3}>
                <p className="text-[#d1d5dc]">
                  The proposed model predicts optimal prices by analyzing
                  variables such as location, seasonality, room type, guest
                  reviews, and market trends. The project consists of two
                  phases: Phase One includes primary and secondary research, and
                  Phase Two focuses on developing and deploying the predictive
                  model.
                </p>
              </TextReveal>
            </ParallaxText>
            <ParallaxText offset={10}>
              <TextReveal delay={0.4}>
                <p className="text-[#d1d5dc]">
                  Primary research through surveys and interviews revealed the
                  lack of user-friendly pricing tools and difficulty balancing
                  profitability with competitive pricing. Secondary research
                  analyzed existing tools such as AirDNA, Wheelhouse, Beyond
                  Pricing, and Pricelabs, and academic models like Random Forest
                  and Gradient Boosting. Current solutions rely heavily on
                  historical data and lack adaptability.
                </p>
              </TextReveal>
            </ParallaxText>
            <ParallaxText offset={5}>
              <TextReveal delay={0.5}>
                <p className="text-[#d1d5dc]">
                  This project aims to provide a more intelligent, real-time
                  model that combines machine learning, sentiment analysis, and
                  geospatial data. The outcome will help hosts improve pricing
                  decisions, increase revenue, and stay competitive in the
                  market.
                </p>
              </TextReveal>
            </ParallaxText>
          </div>
        </div>
      </Section>
      {/* Introduction */}
      <Section id="introduction">
        <div className="max-w-7xl mx-auto">
          <SectionHeader number="" title="Introduction" />
          <div className="mt-6 md:mt-12 space-y-4">
            <ParallaxText offset={20}>
              <TextReveal delay={0.2}>
                <p className="text-[#d1d5dc]">
                  Platforms like Airbnb have transformed the rental market. For
                  hosts, pricing is crucial—pricing too low reduces revenue, and
                  pricing too high reduces occupancy. Many hosts still rely on
                  guesswork or limited tools such as Airbnb's Smart Pricing,
                  which is often seen as restrictive or non-transparent.
                </p>
              </TextReveal>
            </ParallaxText>
            <ParallaxText offset={15}>
              <TextReveal delay={0.3}>
                <p className="text-[#d1d5dc]">
                  This project addresses the need for a more transparent and
                  data-driven pricing approach that supports hosts in maximizing
                  profit and bookings.
                </p>
              </TextReveal>
            </ParallaxText>
          </div>
        </div>
      </Section>

      {/* Summary of Findings by Category */}
      <Section
        id="Summary of Findings by Category"
        className="px-4 md:px-8 lg:px-12 xl:px-16 py-12 md:py-16 max-w-7xl mx-auto"
      >
        <TextReveal>
          <h3 className="text-2xl md:text-3xl text-center mb-12 md:mb-16">
            Research Process
          </h3>
        </TextReveal>

        <ParallaxText offset={20}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-8 md:mb-12">
            <ResearchCard
              icon={
                <svg className="w-8 h-8" fill="none" viewBox="0 0 32 32">
                  <path
                    d={svgPaths.p1b197700}
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                  <path
                    d={svgPaths.p305ecd00}
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                  <path
                    d="M13.3327 12H10.666"
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                  <path
                    d="M21.3327 17.3333H10.666"
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                  <path
                    d="M21.3327 22.6667H10.666"
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                </svg>
              }
              title="Literature Review"
              delay={0}
            />

            <ResearchCard
              icon={
                <svg className="w-8 h-8" fill="none" viewBox="0 0 32 32">
                  <path
                    d={svgPaths.p1dcdf880}
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                  <path
                    d="M24 22.6667V12"
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                  <path
                    d="M17.334 22.6667V6.66675"
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                  <path
                    d="M10.666 22.6667V18.6667"
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                </svg>
              }
              title="Lt. Analysis"
              delay={0.1}
            />

            <ResearchCard
              icon={
                <svg className="w-8 h-8" fill="none" viewBox="0 0 32 32">
                  <path
                    d={svgPaths.p1aa07200}
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                  <path
                    d={svgPaths.p1fa92f00}
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                  <path
                    d={svgPaths.p7208300}
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                </svg>
              }
              title="Competitive Analysis"
              delay={0.2}
            />

            <ResearchCard
              icon={
                <svg className="w-8 h-8" fill="none" viewBox="0 0 32 32">
                  <path
                    d={svgPaths.p5dd0300}
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                  <path
                    d={svgPaths.p22f6940}
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                  <path
                    d={svgPaths.p16ae4740}
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                  <path
                    d={svgPaths.p338d3070}
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                </svg>
              }
              title="Primary Research"
              delay={0.3}
            />
          </div>

          <ResearchCard
            icon={
              <svg className="w-8 h-8" fill="none" viewBox="0 0 32 32">
                <path
                  d={svgPaths.p1ebc8740}
                  stroke="#FD5B38"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.66667"
                />
                <path
                  d={svgPaths.p2b383280}
                  stroke="#FD5B38"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.66667"
                />
                <path
                  d={svgPaths.p18671780}
                  stroke="#FD5B38"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.66667"
                />
              </svg>
            }
            title="Secondary Research"
            subtitle="(Airbnb Dataset)"
            delay={0.4}
          />
        </ParallaxText>
      </Section>

      {/* Summary of Findings Section */}
      <Section
        id="Summary of Findings by Category 2"
        className="px-4 md:px-8 lg:px-12 xl:px-16 py-12 md:py-16 lg:py-20 max-w-7xl mx-auto"
      >
        <TextReveal>
          <h3 className="text-2xl md:text-3xl text-center mb-12 md:mb-16">
            Summary of Findings by Category
          </h3>
        </TextReveal>

        <ParallaxText offset={15}>
          <div className="space-y-6 md:space-y-8">
            {/* Category: Location */}
            <FindingCard
              icon={
                <svg className="w-8 h-8" fill="none" viewBox="0 0 32 32">
                  <path
                    d={svgPaths.p13211180}
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                  <path
                    d={svgPaths.p363c980}
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                </svg>
              }
              decorativeIcon={
                <svg className="w-12 h-12" fill="none" viewBox="0 0 48 48">
                  <path
                    d={svgPaths.p30cd2b80}
                    stroke="#99A1AF"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="4"
                    opacity="0.2"
                  />
                  <path
                    d={svgPaths.pd151bb0}
                    stroke="#99A1AF"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="4"
                    opacity="0.2"
                  />
                </svg>
              }
              category="Category: Location"
              findings="The study by Perez-Sanchez et al. (2021) found that properties closer to city centers and tourist attractions commanded higher prices. Additionally, neighborhood characteristics, such as safety and accessibility to services, significantly influenced price determination."
              delay={0}
            />

            {/* Category: Property Characteristics */}
            <FindingCard
              icon={
                <svg className="w-8 h-8" fill="none" viewBox="0 0 32 32">
                  <path
                    d={svgPaths.p3b973d80}
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                  <path
                    d={svgPaths.p3d6c5800}
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                </svg>
              }
              decorativeIcon={
                <svg className="w-12 h-12" fill="none" viewBox="0 0 48 48">
                  <path
                    d={svgPaths.p38d06800}
                    stroke="#99A1AF"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="4"
                    opacity="0.2"
                  />
                  <path
                    d={svgPaths.pca93740}
                    stroke="#99A1AF"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="4"
                    opacity="0.2"
                  />
                </svg>
              }
              category="Category: Property Characteristics"
              findings="Studies highlighted the role of property attributes, such as room type and amenities. For instance, Zhu, Li, and Xie (2020) identified property characteristics as essential features in machine learning models for predicting Airbnb prices."
              delay={0.1}
            />

            {/* Category: Guest Reviews */}
            <FindingCard
              icon={
                <svg className="w-8 h-8" fill="none" viewBox="0 0 32 32">
                  <path
                    d={svgPaths.p205a3e80}
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                </svg>
              }
              decorativeIcon={
                <svg className="w-12 h-12" fill="none" viewBox="0 0 48 48">
                  <path
                    d={svgPaths.p2d95a600}
                    stroke="#99A1AF"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="4"
                    opacity="0.2"
                  />
                </svg>
              }
              category="Category: Guest Reviews"
              findings="Luo, Ma, and Zhang (2020) demonstrated that sentiment analysis of guest reviews, reflecting positive or negative feedback, significantly enhanced prediction accuracy by influencing price variations. This study underscored the importance of combining unstructured data (guest reviews) with traditional factors like location."
              delay={0.2}
            />

            {/* Category: Market Conditions */}
            <FindingCard
              icon={
                <svg className="w-8 h-8" fill="none" viewBox="0 0 32 32">
                  <path
                    d={svgPaths.p1be1a000}
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                  <path
                    d={svgPaths.paa22850}
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                </svg>
              }
              decorativeIcon={
                <svg className="w-12 h-12" fill="none" viewBox="0 0 48 48">
                  <path
                    d="M32 14H44V26"
                    stroke="#99A1AF"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="4"
                    opacity="0.2"
                  />
                  <path
                    d="M44 14L27 31L17 21L4 34"
                    stroke="#99A1AF"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="4"
                    opacity="0.2"
                  />
                </svg>
              }
              category="Category: Market Conditions"
              findings="Wang and Nicolau (2017) conducted a broad analysis across 33 cities, concluding that guest reviews and location were primary variables impacting price. They also noted that Airbnb hosts could dynamically adjust prices, an advantage over traditional hotels in response to market conditions."
              delay={0.3}
            />

            {/* Category: Host Reputation */}
            <FindingCard
              icon={
                <svg className="w-8 h-8" fill="none" viewBox="0 0 32 32">
                  <path
                    d={svgPaths.p1cd211b1}
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                </svg>
              }
              decorativeIcon={
                <svg className="w-12 h-12" fill="none" viewBox="0 0 48 48">
                  <path
                    d={svgPaths.p2c62d580}
                    stroke="#99A1AF"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="4"
                    opacity="0.2"
                  />
                </svg>
              }
              category="Category: Host Reputation"
              findings="Wang and Nicolau's study also emphasized that host factors like reputation, responsiveness, and communication quality contributed to price variability, impacting trust and perceived listing value."
              delay={0.4}
            />

            {/* Category: Spatial Data */}
            <FindingCard
              icon={
                <svg className="w-8 h-8" fill="none" viewBox="0 0 32 32">
                  <path
                    d={svgPaths.p6483d80}
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                  <path
                    d="M20 7.6875V27.6875"
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                  <path
                    d="M12 4.3125V24.3125"
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                </svg>
              }
              decorativeIcon={
                <svg className="w-12 h-12" fill="none" viewBox="0 0 48 48">
                  <path
                    d={svgPaths.p8a5fec0}
                    stroke="#99A1AF"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="4"
                    opacity="0.2"
                  />
                  <path
                    d="M30 11.5312V41.5312"
                    stroke="#99A1AF"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="4"
                    opacity="0.2"
                  />
                  <path
                    d="M18 6.46875V36.4688"
                    stroke="#99A1AF"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="4"
                    opacity="0.2"
                  />
                </svg>
              }
              category="Category: Spatial Data"
              findings="Jiang et al. (2022) used random forest models to integrate spatial data (proximity to attractions) with text features from customer reviews. They found that spatial embeddings improved price predictions, showing the geographic influence on Airbnb prices."
              delay={0.5}
            />

            {/* Category: Machine Learning Model Use */}
            <FindingCard
              icon={
                <svg className="w-8 h-8" fill="none" viewBox="0 0 32 32">
                  <path
                    d="M16 26.6667V29.3333"
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                  <path
                    d="M16 2.66667V5.33333"
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                  <path
                    d="M22.666 26.6665V29.3332"
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                  <path
                    d="M22.666 2.6665V5.33317"
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                  <path
                    d="M2.66602 16H5.33268"
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                  <path
                    d="M2.66602 22.6665H5.33268"
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                  <path
                    d="M2.66602 9.3335H5.33268"
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                  <path
                    d="M26.666 16H29.3327"
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                  <path
                    d="M26.666 22.6665H29.3327"
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                  <path
                    d="M26.666 9.3335H29.3327"
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                  <path
                    d="M9.33398 26.6665V29.3332"
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                  <path
                    d="M9.33398 2.6665V5.33317"
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                  <path
                    d={svgPaths.p11a77980}
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                  <path
                    d={svgPaths.p223d5280}
                    stroke="#FD5B38"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.66667"
                  />
                </svg>
              }
              decorativeIcon={
                <svg className="w-12 h-12" fill="none" viewBox="0 0 48 48">
                  <path
                    d="M24 40V44"
                    stroke="#99A1AF"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="4"
                    opacity="0.2"
                  />
                  <path
                    d="M24 4V8"
                    stroke="#99A1AF"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="4"
                    opacity="0.2"
                  />
                  <path
                    d="M34 40V44"
                    stroke="#99A1AF"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="4"
                    opacity="0.2"
                  />
                  <path
                    d="M34 4V8"
                    stroke="#99A1AF"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="4"
                    opacity="0.2"
                  />
                  <path
                    d="M4 24H8"
                    stroke="#99A1AF"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="4"
                    opacity="0.2"
                  />
                  <path
                    d="M4 34H8"
                    stroke="#99A1AF"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="4"
                    opacity="0.2"
                  />
                  <path
                    d="M4 14H8"
                    stroke="#99A1AF"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="4"
                    opacity="0.2"
                  />
                  <path
                    d="M40 24H44"
                    stroke="#99A1AF"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="4"
                    opacity="0.2"
                  />
                  <path
                    d="M40 34H44"
                    stroke="#99A1AF"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="4"
                    opacity="0.2"
                  />
                  <path
                    d="M40 14H44"
                    stroke="#99A1AF"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="4"
                    opacity="0.2"
                  />
                  <path
                    d="M14 40V44"
                    stroke="#99A1AF"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="4"
                    opacity="0.2"
                  />
                  <path
                    d="M14 4V8"
                    stroke="#99A1AF"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="4"
                    opacity="0.2"
                  />
                  <path
                    d={svgPaths.p3e330b80}
                    stroke="#99A1AF"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="4"
                    opacity="0.2"
                  />
                  <path
                    d={svgPaths.p25965800}
                    stroke="#99A1AF"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="4"
                    opacity="0.2"
                  />
                </svg>
              }
              category="Category: Machine Learning Model Use"
              findings="Various models, including Random Forest and Gradient Boosting, were identified as effective. For example, De Lima and Da Silva (2021) found that Random Forest handled large datasets well, managing complex interactions like spatial and neighbourhood characteristics, which are important for diverse Airbnb settings."
              delay={0.6}
            />
          </div>
        </ParallaxText>
      </Section>

      {/* Background and Context */}
      <Section id="significance-of-the-study">
        <div className="max-w-7xl mx-auto">
          <SectionHeader number="" title="Background and Context" />
          <div className="mt-6 md:mt-12 space-y-4">
            <ParallaxText offset={20}>
              <TextReveal delay={0.2}>
                <p className="text-[#d1d5dc]">
                  Airbnb operates in over 190 countries and more than 100,000
                  cities, reshaping the hospitality industry. The platform's
                  decentralized model brings diversity and flexibility but also
                  increases pricing complexity. Unlike hotels, hosts lack
                  centralized pricing systems and frequently use manual
                  adjustments or trial-and-error methods.
                </p>
              </TextReveal>
            </ParallaxText>
            <div className="space-y-6 mb-10 sm:mb-12 md:mb-16">
              <ParallaxText delay={0.2}>
                <p className="text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed max-w-6xl">
                  Airbnb operates in over 190 countries and more than 100,000
                  cities, reshaping the hospitality industry. The platform's
                  decentralized model brings diversity and flexibility but also
                  increases pricing complexity. Unlike hotels, hosts lack
                  centralized pricing systems and frequently use manual
                  adjustments or trial-and-error methods.
                </p>
              </ParallaxText>

              <ParallaxText delay={0.3}>
                <p className="text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed my-8 md:my-12">
                  Several factors influence pricing:
                </p>
              </ParallaxText>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10 sm:mb-12 md:mb-16">
              <FeatureCard delay={0.4}>
                <div className="flex items-center gap-3">
                  <span className="text-[#FD5B38] text-xl flex-shrink-0">
                    •
                  </span>
                  <span className="text-sm sm:text-base text-muted-foreground">
                    Location and proximity to attractions
                  </span>
                </div>
              </FeatureCard>
              <FeatureCard delay={0.5}>
                <div className="flex items-center gap-3">
                  <span className="text-[#FD5B38] text-xl flex-shrink-0">
                    •
                  </span>
                  <span className="text-sm sm:text-base text-muted-foreground">
                    Property type and amenities
                  </span>
                </div>
              </FeatureCard>
              <FeatureCard delay={0.6}>
                <div className="flex items-center gap-3">
                  <span className="text-[#FD5B38] text-xl flex-shrink-0">
                    •
                  </span>
                  <span className="text-sm sm:text-base text-muted-foreground">
                    Guest reviews and ratings
                  </span>
                </div>
              </FeatureCard>
              <FeatureCard delay={0.7}>
                <div className="flex items-center gap-3">
                  <span className="text-[#FD5B38] text-xl flex-shrink-0">
                    •
                  </span>
                  <span className="text-sm sm:text-base text-muted-foreground">
                    Seasonality and local events
                  </span>
                </div>
              </FeatureCard>
              <FeatureCard delay={0.8}>
                <div className="flex items-center gap-3">
                  <span className="text-[#FD5B38] text-xl flex-shrink-0">
                    •
                  </span>
                  <span className="text-sm sm:text-base text-muted-foreground">
                    Market trends and travel behavior
                  </span>
                </div>
              </FeatureCard>
            </div>
            <ParallaxText delay={0.9}>
              <p className="text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed max-w-6xl my-8 md:my-12">
                These factors highlight the need for a pricing tool that adapts
                to real-time market conditions rather than relying only on
                historical data.
              </p>
            </ParallaxText>
          </div>
          {/* Project Objective Section */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-transparent pointer-events-none" />
            <AnimatedSection className="max-w-8xl mx-auto relative">
              <div className="py-8 md:py-12 lg:py-16">
                <SectionHeader number="" title="Project Objective" />
              </div>
              <ParallaxText delay={0.2}>
                <p className="text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed max-w-6xl mb-10 sm:mb-12 md:mb-16">
                  The goal is to build an intelligent pricing model using
                  machine learning algorithms such as Random Forest and Gradient
                  Boosting. The model will analyze structured (location, room
                  type) and unstructured (guest reviews, sentiment) data to
                  generate personalized pricing suggestions.
                </p>
              </ParallaxText>

              <div className="mb-8 sm:mb-10">
                <ParallaxText delay={0.3}>
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-normal text-white mt-6 md:mt-12 md:mb-12 mb-4">
                    Research Questions
                  </h3>
                </ParallaxText>
              </div>

              <div className="space-y-4">
                <QuestionCard
                  number="1"
                  question="What variables most influence Airbnb pricing?"
                  delay={0.4}
                />
                <QuestionCard
                  number="2"
                  question="Can machine learning improve prediction accuracy?"
                  delay={0.5}
                />
                <QuestionCard
                  number="3"
                  question="What gaps exist in current tools, and how can they be improved?"
                  delay={0.6}
                />
              </div>
            </AnimatedSection>
          </div>

          {/* Scope of the Research Section */}
          <AnimatedSection className="max-w-8xl mx-auto">
            <div className="py-8 md:py-12 lg:py-16">
              <SectionHeader number="" title="Scope of the Research" />
            </div>

            <ParallaxText delay={0.2}>
              <p className="text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed mb-12 sm:mb-12 md:mb-16">
                The scope of this research encompasses three core areas:
              </p>
            </ParallaxText>

            <div className="space-y-6">
              <DetailCard number="1" title="Data Collection:" delay={0.3}>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <span className="text-[#FD5B38] text-base mt-1">•</span>
                    <p className="text-muted-foreground text-base leading-relaxed">
                      <span className="text-white font-bold">
                        Primary Data:
                      </span>{" "}
                      Surveys and interviews with Airbnb hosts to gather
                      firsthand insights into pricing challenges and
                      expectations.
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-[#FD5B38] text-base mt-1">•</span>
                    <p className="text-muted-foreground text-base leading-relaxed">
                      <span className="text-white font-bold">
                        Secondary Data:
                      </span>{" "}
                      Analysis of publicly available Airbnb datasets (e.g.,
                      Airbnb NYC dataset) to identify pricing patterns and
                      influential factors.
                    </p>
                  </div>
                </div>
              </DetailCard>

              <DetailCard number="2" title="Competitive Landscape:" delay={0.4}>
                <div className="flex items-start gap-3">
                  <span className="text-[#FD5B38] text-base mt-1">•</span>
                  <p className="text-muted-foreground text-base leading-relaxed">
                    Examination of existing pricing platforms to evaluate their
                    features, strengths, and weaknesses, providing a benchmark
                    for the proposed model.
                  </p>
                </div>
              </DetailCard>

              <DetailCard
                number="3"
                title="Machine Learning Models:"
                delay={0.5}
              >
                <div className="flex items-start gap-3">
                  <span className="text-[#FD5B38] text-base mt-1">•</span>
                  <p className="text-muted-foreground text-base leading-relaxed">
                    Exploration of algorithms like Random Forest and Gradient
                    Boosting Regression to build predictive models. These will
                    be trained and tested on the collected data to assess their
                    accuracy and practical utility.
                  </p>
                </div>
              </DetailCard>
            </div>
          </AnimatedSection>

          {/* Research Methodology Section */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-transparent pointer-events-none" />
            <AnimatedSection className="max-w-8xl mx-auto relative">
              <div className="py-8 md:py-12 lg:py-16">
                <SectionHeader number="" title="Research Methodology" />
              </div>

              <ParallaxText delay={0.2}>
                <p className="text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed mb-8 sm:mb-12 md:mb-16">
                  The research employs a mixed-methods approach, combining:
                </p>
              </ParallaxText>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <DetailCard number="1" title="Secondary Research:" delay={0.3}>
                  <p className="text-muted-foreground text-base leading-relaxed">
                    A literature review of studies on Airbnb pricing and machine
                    learning models to establish a theoretical foundation.
                  </p>
                </DetailCard>

                <DetailCard number="2" title="Primary Research:" delay={0.4}>
                  <p className="text-muted-foreground text-base leading-relaxed">
                    Surveys and interviews with Airbnb hosts to uncover
                    qualitative and quantitative insights about current pricing
                    practices and challenges.
                  </p>
                </DetailCard>
              </div>
            </AnimatedSection>
          </div>

          {/* Expected Outcomes Section */}
          <AnimatedSection className="max-w-8xl mx-auto">
            <div className="py-8 md:py-12 lg:py-16">
              <SectionHeader number="" title="Expected Outcomes" />
            </div>

            <ParallaxText delay={0.2}>
              <p className="text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed mb-8 sm:mb-12 md:mb-16">
                The expected outcomes of this research phase are:
              </p>
            </ParallaxText>

            <div className="space-y-6">
              <OutcomeCard
                number="1"
                title="Identification of Key Pricing Factors:"
                description="Comprehensive understanding of the most influential variables in Airbnb pricing."
                delay={0.3}
              />
              <OutcomeCard
                number="2"
                title="Evaluation of Existing Tools:"
                description="Detailed analysis of the strengths and limitations of current pricing platforms."
                delay={0.4}
              />
              <OutcomeCard
                number="3"
                title="Foundation for a Predictive Model:"
                description="Development of a prototype pricing model using machine learning, tailored to the needs of Airbnb hosts."
                delay={0.5}
              />
            </div>
          </AnimatedSection>
        </div>
      </Section>

      {/* Significance of the Study */}
      <Section id="significance-of-the-study">
        <div className="max-w-7xl mx-auto">
          <SectionHeader number="" title="Significance of the Study" />
          <div className="mt-6 md:mt-12 space-y-4">
            <ParallaxText offset={20}>
              <TextReveal delay={0.2}>
                <p className="text-[#d1d5dc]">
                  This study is significant because it aims to address a
                  critical gap within the short-term rental market—specifically,
                  the absence of accessible and data-driven pricing tools for
                  individual Airbnb hosts. Although several pricing platforms
                  exist, many rely largely on historical data and rules-based
                  adjustments. They often fail to consider real-time dynamics,
                  guest sentiment, or localized market conditions. This creates
                  substantial challenges for hosts who lack technical expertise
                  or the financial resources to adopt advanced tools.
                </p>
              </TextReveal>
            </ParallaxText>
            <ParallaxText offset={15}>
              <TextReveal delay={0.3}>
                <p className="text-[#d1d5dc]">
                  By designing a pricing model powered by machine learning, the
                  proposed approach enables Airbnb hosts to make informed and
                  adaptive pricing decisions. Integrating diverse datasets such
                  as location, reviews, and seasonal events will help hosts
                  improve revenue potential while remaining competitive in
                  rapidly changing market conditions. Furthermore, the inclusion
                  of guest sentiment and contextual features contributes to a
                  novel pricing methodology that goes beyond existing tool
                  capabilities.
                </p>
              </TextReveal>
            </ParallaxText>
            <ParallaxText offset={15}>
              <TextReveal delay={0.3}>
                <p className="text-[#d1d5dc]">
                  At a broader level, the study contributes to the growing
                  academic landscape of dynamic pricing, predictive analytics,
                  and the sharing economy. With vacation rentals experiencing
                  increasing competition and variability, this research offers
                  practical solutions for hosts while advancing theoretical
                  insights in the field. In essence, the objective is to make
                  Airbnb hosting more transparent, profitable, and sustainable
                  through intelligent, user-friendly pricing technology.
                </p>
              </TextReveal>
            </ParallaxText>
          </div>
        </div>
      </Section>

      {/* Competitive Analysis */}
      <Section id="competitive-analysis">
        <div className="max-w-7xl mx-auto px-4">
          <SectionHeader number="" title="Competitive Analysis" />
          <ParallaxText>
            <div className="space-y-6">
              <TextReveal delay={0.1}>
                <h3 className="text-xl md:text-2xl lg:text-3xl mt-8 md:mt-12 mb-6">
                  Introduction
                </h3>
              </TextReveal>

              <TextReveal delay={0.2}>
                <p className="text-[#d1d5dc] text-base md:text-lg lg:text-xl leading-relaxed">
                  The market for Airbnb pricing software has grown
                  substantially, with platforms such as AirDNA, Beyond Pricing,
                  Wheelhouse, and Pricelabs offering advanced analytics. These
                  systems typically include features like price forecasts,
                  occupancy rates, and revenue analysis. However, they share
                  limitations including limited personalization, minimal
                  real-time responsiveness, and lack of sentiment-based
                  adjustments. This competitive landscape highlights the need
                  for a model that integrates more dynamic and diverse data
                  sources.
                </p>
              </TextReveal>

              <TextReveal delay={0.3}>
                <h3 className="text-xl md:text-2xl lg:text-3xl mt-12">
                  Analysis of Existing Tools
                </h3>
              </TextReveal>

              <TextReveal delay={0.4}>
                <div className="mt-8 space-y-4">
                  <CompetitorRow
                    isHeader={true}
                    platform=""
                    icon=""
                    overview=""
                    features=""
                    strengths=""
                    weaknesses=""
                    pricing=""
                    insights=""
                  />
                  <CompetitorRow
                    platform="AirDNA"
                    icon={
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24">
                        <path
                          d={svgPaths.p36c5af80}
                          stroke="#FD5B38"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        />
                        <path
                          d="M18 17V9"
                          stroke="#FD5B38"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        />
                        <path
                          d="M13 17V5"
                          stroke="#FD5B38"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        />
                        <path
                          d="M8 17V14"
                          stroke="#FD5B38"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        />
                      </svg>
                    }
                    overview="AirDNA is a data analytics platform for investors and property managers, offering market insights into short-term rental performance across global markets."
                    features="Predictive analytics, market trend tracking, property benchmarking, and detailed reports on revenue and occupancy."
                    strengths="Comprehensive market data and global coverage, making it ideal for investors and professional managers."
                    weaknesses="Expensive subscription tiers; complex interface unsuitable for individual Airbnb hosts who lack experience with analytics tools."
                    pricing="Paid: Subscription tiers based on data access level."
                    insights="AirDNA excels in large-scale market trend analysis, offering valuable insights for property managers, but it lacks affordability and simplicity for smaller hosts."
                  />

                  <CompetitorRow
                    platform="Beyond Pricing"
                    icon={
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 20.5875 20.5875"
                      >
                        <path
                          d={svgPaths.p1d7b180}
                          stroke="#FD5B38"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="1.71563"
                        />
                        <path
                          d={svgPaths.p3c692580}
                          stroke="#FD5B38"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="1.71563"
                        />
                      </svg>
                    }
                    overview="Beyond Pricing is a dynamic pricing platform focused on automating pricing adjustments for short-term rental hosts based on market trends and booking data."
                    features="Automated daily pricing updates, market-driven revenue optimization, and insights into demand fluctuations."
                    strengths="Simple, automated integration with platforms like Airbnb and VRBO; reduces manual workload with effective revenue management tools."
                    weaknesses="Heavy reliance on historical data, which may limit its ability to predict prices accurately during unforeseen events or rapid demand shifts."
                    pricing="Paid: Revenue share model with 1% of monthly earnings charged to users."
                    insights="Beyond Pricing offers effortless automation for revenue optimization but lacks adaptability to non-historical data, leaving room for innovation in real-time factors."
                  />

                  <CompetitorRow
                    platform="Wheelhouse"
                    icon={
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24">
                        <path
                          d={svgPaths.p327fad80}
                          stroke="#FD5B38"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        />
                      </svg>
                    }
                    overview="Wheelhouse is a user-friendly pricing tool designed for Airbnb hosts, offering dynamic pricing, recommendations, and actionable market insights."
                    features="Dynamic pricing adjustments, localized market data, and an intuitive dashboard for easy data visualization."
                    strengths="Highly intuitive interface, making it accessible to both new and experienced Airbnb hosts. Reduces manual intervention with automated pricing updates."
                    weaknesses="Limited niche market insights; moderate data customization options restrict its flexibility for advanced users or property managers managing multiple listings."
                    pricing="Paid: Monthly subscription based on number of properties, with a free trial option available."
                    insights="Wheelhouse stands out for its simplicity and accessibility, ideal for hosts seeking user-friendly pricing tools but limited in its advanced features compared to other platforms."
                  />

                  <CompetitorRow
                    platform="Pricelabs"
                    icon={
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24">
                        <path
                          d={svgPaths.pace200}
                          stroke="#FD5B38"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        />
                        <path
                          d={svgPaths.p3c6311f0}
                          stroke="#FD5B38"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        />
                        <path
                          d={svgPaths.p3d728000}
                          stroke="#FD5B38"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        />
                      </svg>
                    }
                    overview="Pricelabs is a customizable pricing tool that combines real-time data with advanced analytics, making it a popular choice for experienced property managers."
                    features="Customizable pricing rules, real-time market integration, and advanced analytics tailored for experienced hosts."
                    strengths="High flexibility in pricing adjustments; integrates real-time market data effectively, ensuring pricing strategies remain aligned with demand trends."
                    weaknesses="Steep learning curve for beginners; advanced customization features require time and effort to master, making it less accessible to individual hosts without prior experience."
                    pricing="Paid: Flexible subscription plans based on the number of listings managed."
                    insights="Pricelabs offers unparalleled customization for advanced users but is less suitable for hosts seeking out-of-the-box pricing recommendations or simpler tools."
                  />

                  <CompetitorRow
                    platform="Our Model"
                    icon={
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24">
                        <path
                          d="M12 20V22"
                          stroke="#FD5B38"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        />
                        <path
                          d="M12 2V4"
                          stroke="#FD5B38"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        />
                        <path
                          d="M17 20V22"
                          stroke="#FD5B38"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        />
                        <path
                          d="M17 2V4"
                          stroke="#FD5B38"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        />
                        <path
                          d="M2 12H4"
                          stroke="#FD5B38"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        />
                        <path
                          d="M2 17H4"
                          stroke="#FD5B38"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        />
                        <path
                          d="M2 7H4"
                          stroke="#FD5B38"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        />
                        <path
                          d="M20 12H22"
                          stroke="#FD5B38"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        />
                        <path
                          d="M20 17H22"
                          stroke="#FD5B38"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        />
                        <path
                          d="M20 7H22"
                          stroke="#FD5B38"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        />
                        <path
                          d="M7 20V22"
                          stroke="#FD5B38"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        />
                        <path
                          d="M7 2V4"
                          stroke="#FD5B38"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        />
                        <path
                          d={svgPaths.pc216af0}
                          stroke="#FD5B38"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        />
                        <path
                          d={svgPaths.p10ba7d80}
                          stroke="#FD5B38"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        />
                      </svg>
                    }
                    overview="Our model provides a machine learning-powered pricing tool specifically tailored for Airbnb hosts, integrating structured data (location, room type) with unstructured data (guest reviews)."
                    features="Advanced machine learning models (Random Forest, Gradient Boosting), guest sentiment analysis through NLP, and transparent, actionable insights for Airbnb hosts."
                    strengths="Combines machine learning with sentiment analysis to provide real-time, adaptive pricing recommendations. Transparent visualizations make insights accessible even to new hosts."
                    weaknesses="Requires significant data preprocessing and large dataset for model training. Initial implementation may present challenges in balancing automation with user-defined customization."
                    pricing="TBD: Likely free to individual hosts with potential for pay-per-use or subscription-based upgrades."
                    insights="Our model bridges the gap between simplicity and advanced analytics by offering real-time adaptability, sentiment-driven insights, and transparent recommendations for Airbnb hosts."
                    highlight
                  />
                </div>
              </TextReveal>

              <TextReveal delay={0.5}>
                <div className="mt-12 space-y-6 p-6 md:p-8 rounded-2xl bg-gradient-to-br from-white/5 to-transparent item-border border-white/10">
                  <h3 className="text-xl md:text-2xl lg:text-3xl">
                    Machine Learning and Dynamic Pricing
                  </h3>
                  <p className="text-[#d1d5dc] text-base md:text-lg lg:text-xl leading-relaxed">
                    Machine learning models provide significant advantages over
                    static tools because they can learn complex interactions
                    between variables and update pricing in real time. Two
                    particularly strong models include:
                  </p>
                  <ul className="space-y-4 pl-6">
                    <li className="text-[#d1d5dc] text-base md:text-lg leading-relaxed">
                      <span className="text-white">
                        <span className="text-[#FD5B38]">•</span>{" "}
                        &nbsp;&nbsp;Random Forest Regressor:
                      </span>{" "}
                      highly robust, handles mixed data types, ranks feature
                      importance.
                    </li>
                    <li className="text-[#d1d5dc] text-base md:text-lg leading-relaxed">
                      <span className="text-white">
                        <span className="text-[#FD5B38]">•</span>{" "}
                        &nbsp;&nbsp;Gradient Boosting Regression:
                      </span>{" "}
                      excels at non-linear prediction patterns and understanding
                      demand behavior.
                    </li>
                  </ul>
                  <p className="text-[#d1d5dc] text-base md:text-lg lg:text-xl leading-relaxed">
                    Sentiment analysis offers another competitive dimension by
                    interpreting guest feedback. Positive sentiment may justify
                    higher prices, while negative sentiment may indicate the
                    need for price adjustments.
                  </p>
                  <p className="text-[#d1d5dc] text-base md:text-lg lg:text-xl leading-relaxed">
                    Dynamic pricing strategies, already widely used in airlines
                    and hotels, are becoming essential in short-term rentals due
                    to rapid fluctuations in demand.
                  </p>
                </div>
              </TextReveal>

              <div className="pt-8 md:pt-12 lg:pt-16">
                <div className="p-4 bg-[linear-gradient(135deg,rgba(253,91,56,0.1)_0%,rgba(253,91,56,0.05)_100%)] item-border border-[#FD5B3833] rounded-2xl">
                  <TextReveal delay={0.6}>
                    <h3 className="text-xl md:text-2xl lg:text-3xl">
                      Key Insights from Secondary Research
                    </h3>
                  </TextReveal>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
                    <InsightCard delay={0.7}>
                      Market pricing depends significantly on location, reviews,
                      and property type.
                    </InsightCard>

                    <InsightCard delay={0.8}>
                      Machine learning outperforms rule-based pricing by
                      integrating diverse data sources.
                    </InsightCard>

                    <InsightCard delay={0.9}>
                      Real-time adaptability is crucial for responding to sudden
                      market shifts.
                    </InsightCard>

                    <InsightCard delay={1.0}>
                      Sentiment analysis provides a competitive edge by
                      incorporating guest feedback into pricing strategies.
                    </InsightCard>
                  </div>
                </div>
              </div>
            </div>
          </ParallaxText>
        </div>
      </Section>

      {/* interature-review */}
      <Section id="literature-review">
        <div className="min-h-screenoverflow-x-hidden overflow-y-hidden">
          <ParallaxSection speed={0.3}>
            <Section className="py-12 md:py-16 lg:py-20">
              <div className="max-w-7xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                  className="px-2"
                >
                  <SectionHeader title="Secondary Research: Literature Review" />
                </motion.div>

                <div className="py-12 md:py-16 lg:py-20">
                  {/* Introduction Section */}
                  <ParallaxSection speed={0.2}>
                    <ContentSection title="Introduction to Airbnb Pricing and Machine Learning">
                      <Paragraph>
                        The emergence of the sharing economy, particularly
                        platforms like Airbnb, has significantly influenced
                        traditional hospitality models. Unlike hotels—which
                        typically use centralized and standardized pricing
                        systems—Airbnb hosts operate with considerable
                        flexibility but limited guidance. This results in
                        inconsistent pricing practices and uncertainty in
                        choosing competitive rates.
                      </Paragraph>
                      <Paragraph>
                        Researchers emphasize the importance of data-driven
                        approaches to overcome these challenges. Machine
                        learning models are particularly suitable for predicting
                        Airbnb prices due to their ability to analyze diverse
                        data sources such as property features, reviews, and
                        geographical factors. This literature review evaluates
                        the leading academic contributions focused on machine
                        learning methodologies, feature engineering, and price
                        determinants.
                      </Paragraph>
                    </ContentSection>
                  </ParallaxSection>

                  <div className="space-y-8 mt-8">
                    {/* Machine Learning Models Section */}
                    <ParallaxSection speed={0.25}>
                      <ContentSection
                        title="Machine Learning Models and Feature Engineering"
                        variant="card"
                        delay={0.1}
                      >
                        <Paragraph>
                          One of the earliest large-scale studies in this domain
                          is by Zhu, Li, and Xie (2020), who analyzed more than
                          49,000 listings in New York City. They evaluated
                          multiple machine learning models including Random
                          Forest, Multilayer Perceptron (MLP), and Gradient
                          Boosting Regression. Their findings demonstrated that
                          Gradient Boosting achieved the highest predictive
                          accuracy. The study also confirmed the strong
                          relevance of location, room type, and reviews as core
                          pricing determinants.
                        </Paragraph>
                        <Paragraph>
                          Further contributing to this field, Luo, Ma, and Zhang
                          (2020) integrated sentiment analysis into predictive
                          frameworks using neural networks, clustering
                          techniques, and SVM models. Their study covered
                          listings in major cities such as Paris and Berlin.
                          They concluded that including sentiment analysis of
                          guest reviews significantly improved model accuracy,
                          demonstrating the value of combining structured and
                          unstructured data.
                        </Paragraph>
                      </ContentSection>
                    </ParallaxSection>

                    {/* Determinants of Pricing Section */}
                    <ParallaxSection speed={0.2}>
                      <ContentSection
                        title="Determinants of Pricing and Market Trends"
                        variant="card"
                        delay={0.15}
                      >
                        <Paragraph>
                          A global benchmarking study by Wang and Nicolau (2017)
                          investigated pricing determinants across 33 cities
                          using OLS and quantile regression. The authors found
                          that guest reviews and location were the most
                          influential features in explaining price variations,
                          reinforcing the need to include both quantitative and
                          qualitative attributes within pricing models.
                        </Paragraph>
                        <Paragraph>
                          In a more localized geographical analysis,
                          Perez-Sanchez et al. (2021) examined how neighborhood
                          characteristics, proximity to attractions, and
                          surrounding safety influenced price differences. They
                          concluded that listings closer to landmarks or
                          high-quality neighborhoods consistently commanded
                          higher prices—a key consideration for dynamic pricing.
                        </Paragraph>
                      </ContentSection>
                    </ParallaxSection>

                    {/* Integrating Structured and Unstructured Data Section */}
                    <ParallaxSection speed={0.3}>
                      <ContentSection
                        title="Integrating Structured and Unstructured Data"
                        variant="card"
                        delay={0.2}
                      >
                        <Paragraph>
                          Recent work has highlighted the benefits of combining
                          structured and unstructured data for improved
                          predictive performance. Kalehbasti et al. (2019) used
                          NLP and machine learning to integrate guest reviews
                          with traditional data such as room type and location.
                          Their results indicated that incorporating sentiment
                          yielded more accurate predictions than structured data
                          alone.
                        </Paragraph>
                        <Paragraph>
                          Similarly, Ahuja, Lahiri, and Das (2021) implemented
                          deep learning models using multiple feature modalities
                          including geolocation and property images. Their study
                          demonstrated how diverse feature groups strengthen
                          predictive capability and improve model reliability
                          across markets.
                        </Paragraph>
                      </ContentSection>
                    </ParallaxSection>

                    {/* Advanced Models Section */}
                    <ParallaxSection speed={0.25}>
                      <ContentSection
                        title="Advanced Models: Spatial, Statistical, and Ensemble Techniques"
                        variant="card"
                        delay={0.25}
                      >
                        <Paragraph>
                          Ensemble models have proven to be highly effective in
                          Airbnb pricing. De Lima and Da Silva (2021) found that
                          Random Forest delivered the highest accuracy when
                          applied to listings in Lisbon. The model was
                          particularly successful in handling complex and
                          non-linear relationships in large datasets with
                          spatial components.
                        </Paragraph>
                        <Paragraph>
                          In parallel, Jiang et al. (2022) proposed a
                          multi-source learning framework combining spatial,
                          statistical, and textual data using Random Forest.
                          Their work emphasizes that integrating spatial
                          proximity and textual reviews results in more
                          context-aware pricing predictions—an approach that
                          aligns closely with the objectives of this project.
                        </Paragraph>
                      </ContentSection>
                    </ParallaxSection>
                  </div>
                </div>
              </div>
            </Section>
          </ParallaxSection>

          {/* Data Visualization Section */}
          <ParallaxSection speed={0.4}>
            <div className="max-w-7xl mx-auto px-4">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-base md:text-lg text-[#D4B896] text-center mb-12 md:mb-16"
              >
                Secondary Research | NYC Data Set
              </motion.h2>

              {/* Charts Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mb-8 md:mb-12 h-full">
                <ParallaxSection speed={0.15} className="h-full">
                  <BarChart
                    title="Distribution of Listings by Neighborhood Groups"
                    data={neighborhoodData}
                  />
                </ParallaxSection>

                <ParallaxSection speed={0.15}>
                  <PieChart
                    title="Distribution of Listings by Room Type"
                    data={roomTypeData}
                    img="/assets/imgs/case-studies/ipm_1.png"
                  />
                </ParallaxSection>
              </div>

              {/* Full Width Chart */}
              <ParallaxSection speed={0.2}>
                <PieChart
                  title="Distribution of Listings by Availability"
                  data={availabilityData}
                  img="/assets/imgs/case-studies/ipm_2.png"
                />
              </ParallaxSection>
            </div>
          </ParallaxSection>
        </div>
      </Section>

      {/* Primary Research */}
      <Section id="primary-research">
        <div className="max-w-7xl mx-auto px-4">
          <ParallaxSection className="container mx-auto px-4  md:px-6 py-12 md:py-16">
            <SectionHeader number="" title="Primary Research" />
          </ParallaxSection>
          {/* Introduction */}
          <ParallaxSection className="container md:px-6 py-4 md:py-8">
            <ParallaxText offset={20}>
              <h2 className="text-2xl md:text-3xl font-normal mb-6">
                Introduction
              </h2>
            </ParallaxText>
            <ParallaxText offset={15}>
              <p className="text-research-gray text-lg md:text-xl leading-relaxed max-w-5xl">
                Primary research involves directly collecting original data to
                answer specific research questions. In this project, it played a
                crucial role in understanding the pricing challenges faced by
                Airbnb hosts and identifying how a machine learning–based
                pricing model could support them. Two core methods—surveys and
                interviews—were used to collect firsthand insights from hosts.
                These approaches provided meaningful data on pricing practices,
                common difficulties, and the tools currently used, forming the
                basis for developing the proposed pricing model.
              </p>
            </ParallaxText>
          </ParallaxSection>

          {/* Host Demographics */}
          <AnimatedSection className="container md:px-6 py-8 md:py-12">
            <ParallaxText offset={25}>
              <h2 className="text-2xl md:text-3xl font-normal mb-8 md:mb-12">
                Host Demographics with Interviews/Surveys
              </h2>
            </ParallaxText>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <DemographicsTable />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <ExperienceChart />
              </motion.div>
            </div>
          </AnimatedSection>

          {/* Participant Recruitment Strategy */}
          <AnimatedSection className="container md:px-6 py-8 md:py-12">
            <div className="rounded-2xl item-border border-white/10 bg-gradient-to-br from-white/5 to-transparent backdrop-blur-sm p-6 md:p-8 lg:p-10">
              <ParallaxText offset={20}>
                <h2 className="text-2xl md:text-3xl font-normal mb-6 md:mb-8">
                  Participant Recruitment Strategy
                </h2>
              </ParallaxText>

              {/* Target Population */}
              <div className="mb-8">
                <ParallaxText offset={15}>
                  <h3 className="text-xl md:text-2xl font-normal mb-4">
                    Target Population
                  </h3>
                </ParallaxText>
                <ParallaxText offset={10}>
                  <p className="text-research-gray text-base md:text-lg leading-relaxed">
                    The target group consisted of Airbnb hosts with at least six
                    months of experience managing their own pricing. Hosts
                    located in popular tourist destinations were prioritized, as
                    they often encounter more complex challenges in balancing
                    competitive pricing with profitability.
                  </p>
                </ParallaxText>
              </div>

              {/* Recruitment Venues */}
              <div className="mb-8">
                <ParallaxText offset={15}>
                  <h3 className="text-xl md:text-2xl font-normal mb-4">
                    Recruitment Venues
                  </h3>
                </ParallaxText>
                <ParallaxText offset={10}>
                  <p className="text-research-gray text-base md:text-lg mb-4">
                    Participants were recruited through:
                  </p>
                </ParallaxText>
                <ul className="space-y-3 ml-6">
                  {[
                    {
                      title: "Online Airbnb Communities:",
                      desc: "Host-focused forums and Facebook groups.",
                    },
                    {
                      title: "Direct Outreach:",
                      desc: "Personalized messages sent to Airbnb hosts in selected cities via the Airbnb platform.",
                    },
                  ].map((item, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="flex gap-3 items-start text-base md:text-lg"
                    >
                      <span className="text-[#FD5B38] text-2xl leading-none">
                        •
                      </span>
                      <span>
                        <strong className="text-white font-bold">
                          {item.title}
                        </strong>{" "}
                        <span className="text-research-gray">{item.desc}</span>
                      </span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </div>
          </AnimatedSection>

          {/* Research Methods */}
          <AnimatedSection className="container md:px-6 py-8 md:py-12">
            <ParallaxText offset={20}>
              <h2 className="text-2xl md:text-3xl font-normal mb-8">
                Research Methods
              </h2>
            </ParallaxText>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-6">
              {[
                {
                  title: "Screening Survey",
                  desc: "Ensured participants met criteria such as 6+ months of active hosting and direct involvement in pricing decisions.",
                },
                {
                  title: "Personalized Invitations",
                  desc: "Eligible participants received email invitations to join the study.",
                },
                {
                  title: "Compensation",
                  desc: "A $20 gift card was provided for completing a 30–45-minute interview.",
                },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15 }}
                  whileHover={{ scale: 1.02 }}
                  className="rounded-2xl item-border border-white/10 bg-gradient-to-br from-white/5 to-transparent backdrop-blur-sm p-5 md:p-6"
                >
                  <h4 className="text-[#FD5B38] text-lg md:text-xl mb-3">
                    {item.title}
                  </h4>
                  <p className="text-research-gray text-sm md:text-base leading-relaxed">
                    {item.desc}
                  </p>
                </motion.div>
              ))}
            </div>

            <AnimatedSection delay={0.3}>
              <div className="rounded-2xl item-border border-[#FD5B3833] bg-gradient-to-br from-[#FD5B381A] to-[#FD5B380D] p-5 md:p-6">
                <h3 className="text-xl md:text-2xl font-normal mb-3">
                  Recruitment Timeline
                </h3>
                <p className="text-research-light text-lg md:text-xl">
                  Recruitment started two weeks before the interviews and
                  concluded within one month.
                </p>
              </div>
            </AnimatedSection>
          </AnimatedSection>

          {/* Eligibility Criteria */}
          <AnimatedSection className="container md:px-6 py-8 md:py-12">
            <div className="rounded-2xl item-border border-white/10 bg-gradient-to-br from-white/5 to-transparent backdrop-blur-sm p-6 md:p-8 lg:p-10">
              <ParallaxText offset={20}>
                <h2 className="text-2xl md:text-3xl font-normal mb-8">
                  Eligibility Criteria
                </h2>
              </ParallaxText>

              <ul className="space-y-4">
                {[
                  {
                    title: "Eligible Participants:",
                    desc: "Hosts with 6+ months of experience who manage their own pricing in cities or major tourist destinations.",
                  },
                  {
                    title: "Ineligible Participants:",
                    desc: "Hosts with less than six months of experience or those not directly responsible for pricing decisions.",
                  },
                ].map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.15 }}
                    className="flex gap-3 items-start"
                  >
                    <span className="text-[#FD5B38] text-2xl leading-none mt-1">
                      •
                    </span>
                    <div className="text-base md:text-lg">
                      <strong className="text-white font-bold">
                        {item.title}
                      </strong>{" "}
                      <span className="text-research-gray">{item.desc}</span>
                    </div>
                  </motion.li>
                ))}
              </ul>
            </div>
          </AnimatedSection>

          {/* Survey Section */}
          <AnimatedSection className="container md:px-6 py-8 md:py-12">
            <ParallaxText offset={20}>
              <h2 className="text-2xl md:text-3xl font-normal mb-8">Survey</h2>
            </ParallaxText>

            <div className="space-y-6">
              {[
                "To begin the primary research phase, a survey was conducted as the first method for collecting both quantitative and qualitative data. The survey examined Airbnb hosts' backgrounds, experiences with setting and adjusting prices, and the tools they currently rely on for pricing decisions. Its structured format helped capture insights into pricing frequency, pricing approaches, and satisfaction with existing tools.",
                "The survey was distributed across multiple online channels—social media platforms and Airbnb-hosted forums—to ensure participation from a diverse mix of hosts, including both new and experienced users. The online format allowed respondents to answer at their convenience, and anonymity encouraged honest and detailed feedback.",
                "The survey combined closed-ended (quantitative) and open-ended (qualitative) questions to gather comprehensive data on price-setting methods, adjustment frequency, challenges faced, and levels of satisfaction with Airbnb's pricing tools. A Google Form was used, featuring multiple-choice and open-ended questions that captured diverse data about pricing strategies, tool usage, and obstacles hosts encounter.",
              ].map((text, index) => (
                <ParallaxText key={index} offset={12}>
                  <motion.p
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="text-research-gray text-base md:text-lg leading-relaxed"
                  >
                    {text}
                  </motion.p>
                </ParallaxText>
              ))}
            </div>
          </AnimatedSection>

          {/* Survey Questions */}
          <AnimatedSection className="container md:px-6 py-8 md:py-12">
            <div className="rounded-2xl item-border border-white/10 bg-gradient-to-br from-white/5 to-transparent backdrop-blur-sm p-6 md:p-8 lg:p-10">
              <ParallaxText offset={20}>
                <h3 className="text-xl md:text-2xl font-normal mb-6">
                  Key Survey Questions Included:
                </h3>
              </ParallaxText>

              <ul className="py-3">
                {[
                  "How long have you been hosting on Airbnb?",
                  "On a scale of 1 to 5, how satisfied are you with Airbnb platform's pricing tools?",
                  "What type of property do you primarily rent out?",
                  "How frequently do you adjust the price of your listings?",
                  "Rank the following factors in order of importance when setting your listing's price.",
                  "Do you use Airbnb's Smart Pricing feature?",
                  "How do you typically set prices for your listings?",
                  "How does guest feedback impact your listings?",
                  "Do you offer weekly or monthly discounts?",
                  "What is your biggest challenge when setting prices?",
                ].map((question, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -15 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex gap-3 items-start text-base md:text-lg"
                  >
                    <span className="text-[#FD5B38] leading-relaxed">•</span>
                    <span className="text-research-gray">{question}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </AnimatedSection>

          {/* Survey Insights */}
          <AnimatedSection>
            <ParallaxText offset={20}>
              <h2 className="text-2xl md:text-3xl font-normal mb-8">
                Survey Insights & Findings
              </h2>
            </ParallaxText>

            <div className="space-y-6">
              {/* Host Backgrounds */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="rounded-2xl item-border border-white/10 bg-gradient-to-br from-white/5 to-transparent backdrop-blur-sm p-6 md:p-8"
              >
                <h3 className="text-xl md:text-2xl font-normal mb-4">
                  Host Backgrounds and Experience
                </h3>
                <p className="text-research-gray text-base md:text-lg mb-4">
                  The survey revealed varied levels of hosting experience:
                </p>
                <ul className="py-3 ml-6">
                  <li className="text-research-gray text-base md:text-lg">
                    <span className="mr-2 text-[#FD5B38]">•</span>
                    66.7% had hosted for less than a year
                    <ul className="ml-8 mt-2 space-y-2">
                      <li className="text-research-muted">
                        <span className="text-[#FD5B38] mr-2">○</span>
                        38.1%: hosted for less than six months
                      </li>
                      <li className="text-research-muted">
                        <span className="text-[#FD5B38] mr-2">○</span>
                        28.6%: hosted for six months to one year
                      </li>
                    </ul>
                  </li>
                  <li className="text-research-gray text-base md:text-lg">
                    <span className="text-[#FD5B38] mr-2">•</span>
                    Only 4.8% had hosted for more than five years
                  </li>
                </ul>
                <p className="text-research-gray text-base md:text-lg mt-4">
                  This distribution shows that most hosts are relatively new,
                  indicating limited experience in managing listings and setting
                  competitive prices. The findings highlight the need for better
                  tools and support for newer hosts, particularly for pricing
                  optimization and market analysis.
                </p>
              </motion.div>

              {/* Satisfaction */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="rounded-2xl item-border border-white/10 bg-gradient-to-br from-white/5 to-transparent backdrop-blur-sm p-6 md:p-8"
              >
                <h3 className="text-xl md:text-2xl font-normal mb-4">
                  Satisfaction with Pricing Tools
                </h3>
                <p className="text-research-gray text-base md:text-lg mb-4">
                  Satisfaction was measured using a five-point scale:
                </p>
                <ul className="space-y-2 ml-6 mb-4">
                  <li className="text-research-gray text-base md:text-lg">
                    <span className="text-[#FD5B38] mr-2">•</span>
                    90.5% rated their satisfaction as 4 or 5, showing broad
                    appreciation for existing tools
                  </li>
                  <li className="text-research-gray text-base md:text-lg">
                    <span className="text-[#FD5B38] mr-2">•</span>
                    9.5% were neutral, suggesting some uncertainty about tool
                    effectiveness
                  </li>
                </ul>
                <p className="text-research-gray text-base md:text-lg">
                  Comments indicated that many hosts found the tools helpful but
                  were confused by how pricing suggestions were generated. Hosts
                  noted that current tools often overlook unique property
                  characteristics or local market conditions, limiting trust in
                  the recommendations.
                </p>
              </motion.div>

              {/* Property Types */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="rounded-2xl item-border border-white/10 bg-gradient-to-br from-white/5 to-transparent backdrop-blur-sm p-6 md:p-8"
              >
                <h3 className="text-xl md:text-2xl font-normal mb-4">
                  Property Types
                </h3>
                <p className="text-research-gray text-base md:text-lg mb-4">
                  Respondents listed their primary rental types:
                </p>
                <ul className="space-y-2 ml-6 mb-4">
                  <li className="text-research-gray text-base md:text-lg">
                    <span className="text-[#FD5B38] mr-2">•</span>
                    47.6%: entire homes/apartments
                  </li>
                  <li className="text-research-gray text-base md:text-lg">
                    <span className="text-[#FD5B38] mr-2">•</span>
                    42.9%: private rooms
                  </li>
                  <li className="text-research-gray text-base md:text-lg">
                    <span className="text-[#FD5B38] mr-2">•</span>
                    9.5%: shared rooms
                  </li>
                </ul>
                <p className="text-research-gray text-base md:text-lg">
                  These results show a preference for private accommodations,
                  which often require more nuanced pricing strategies. Hosts
                  expressed that current pricing tools do not always provide
                  adequate guidance for larger or private properties.
                </p>
              </motion.div>

              {/* Frequency of Price Adjustments */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="rounded-2xl item-border border-white/10 bg-gradient-to-br from-white/5 to-transparent backdrop-blur-sm p-6 md:p-8"
              >
                <h3 className="text-xl md:text-2xl font-normal mb-4">
                  Frequency of Price Adjustments
                </h3>
                <p className="text-research-gray text-base md:text-lg mb-4">
                  Responses varied widely:
                </p>
                <ul className="space-y-2 ml-6 mb-4">
                  <li className="text-research-gray text-base md:text-lg">
                    <span className="text-[#FD5B38] mr-2">•</span>
                    52.4%: adjust prices only during peak seasons/events
                  </li>
                  <li className="text-research-gray text-base md:text-lg">
                    <span className="text-[#FD5B38] mr-2">•</span>
                    19%: adjust prices daily
                  </li>
                  <li className="text-research-gray text-base md:text-lg">
                    <span className="text-[#FD5B38] mr-2">•</span>
                    19%: adjust weekly
                  </li>
                  <li className="text-research-gray text-base md:text-lg">
                    <span className="text-[#FD5B38] mr-2">•</span>
                    9.5%: rarely adjust prices
                  </li>
                </ul>
                <p className="text-research-gray text-base md:text-lg">
                  This demonstrates that many hosts rely heavily on seasonal
                  trends and external factors. Hosts emphasized that
                  understanding demand shifts often requires manual research,
                  reinforcing the need for real-time, dynamic pricing tools.
                </p>
              </motion.div>

              {/*   Factors Considered in Pricing */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="rounded-2xl item-border border-white/10 bg-gradient-to-br from-white/5 to-transparent backdrop-blur-sm p-6 md:p-8"
              >
                <h3 className="text-xl md:text-2xl font-normal mb-4">
                  Factors Considered in Pricing
                </h3>
                <p className="text-research-gray text-base md:text-lg mb-4">
                  The most influential factors included:
                </p>
                <ul className="space-y-2 ml-6 mb-4">
                  <li className="text-research-gray text-base md:text-lg">
                    <span className="text-[#FD5B38] mr-2">•</span>
                    Occupancy rates – 38.1%
                  </li>
                  <li className="text-research-gray text-base md:text-lg">
                    <span className="text-[#FD5B38] mr-2">•</span>
                    Seasonal trends – 28.6%
                  </li>
                  <li className="text-research-gray text-base md:text-lg">
                    <span className="text-[#FD5B38] mr-2">•</span>
                    Guest reviews/ratings – 19%
                  </li>
                  <li className="text-research-gray text-base md:text-lg">
                    <span className="text-[#FD5B38] mr-2">•</span>
                    Pricing tools – 9.5%
                  </li>
                </ul>
                <p className="text-research-gray text-base md:text-lg">
                  The dominance of occupancy and seasonality highlights the
                  complexities of pricing decisions and the need for tools that
                  integrate real-time demand, local events, and competitor
                  insights.
                </p>
              </motion.div>

              {/* Pricing Challenges - Highlighted */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="rounded-2xl item-border border-[#FD5B3833] bg-gradient-to-br from-[#FD5B381A] to-[#FD5B380D] p-6 md:p-8"
              >
                <h3 className="text-xl md:text-2xl font-normal mb-4">
                  Challenges With Automated Pricing Tools
                </h3>
                <p className="text-research-light text-base md:text-lg mb-4">
                  A recurring theme was difficulty understanding automated
                  pricing suggestions. Hosts found recommendations lacking
                  transparency and felt unsure how factors like location,
                  feedback, or seasonal demand were weighted. Some noted the
                  system's inability to account for:
                </p>
                <ul className="space-y-2 ml-6 mb-4">
                  {[
                    "Slow seasons",
                    "Local events",
                    "Property-specific attributes (e.g., cleaning fees, discounts)",
                  ].map((item, i) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: -15 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 }}
                      className="text-research-light text-base md:text-lg"
                    >
                      <span className="text-[#FD5B38] mr-2">•</span>
                      {item}
                    </motion.li>
                  ))}
                </ul>
                <p className="text-research-light text-base md:text-lg">
                  These issues contribute to hesitancy in relying fully on
                  automated tools.
                </p>
              </motion.div>

              {/* Broader Pricing Challenges */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="rounded-2xl item-border border-white/10 bg-gradient-to-br from-white/5 to-transparent backdrop-blur-sm p-6 md:p-8"
              >
                <h3 className="text-xl md:text-2xl font-normal mb-4">
                  Broader Pricing Challenges
                </h3>
                <p className="text-research-gray text-base md:text-lg mb-4">
                  Beyond tool-related concerns, hosts identified additional
                  challenges:
                </p>
                <ul className="space-y-2 ml-6 mb-4">
                  <li className="text-research-gray text-base md:text-lg">
                    <span className="text-[#FD5B38] mr-2">•</span>
                    Balancing profitability with competitiveness
                  </li>
                  <li className="text-research-gray text-base md:text-lg">
                    <span className="text-[#FD5B38] mr-2">•</span>
                    Managing operational costs (cleaning, maintenance, mortgage)
                  </li>
                  <li className="text-research-gray text-base md:text-lg">
                    <span className="text-[#FD5B38] mr-2">•</span>
                    Navigating industry shifts such as the COVID-19 impact on
                    travel demand
                  </li>
                  <li className="text-research-gray text-base md:text-lg">
                    <span className="text-[#FD5B38] mr-2">•</span>
                    Handling seasonal slowdowns
                  </li>
                  <li className="text-research-gray text-base md:text-lg">
                    <span className="text-[#FD5B38] mr-2">•</span>
                    Monitoring competitor pricing, often a manual and
                    time-consuming task
                  </li>
                </ul>
                <p className="text-research-gray text-base md:text-lg">
                  Hosts expressed strong interest in tools that could automate
                  competitor analysis and provide real-time market insights.
                </p>
              </motion.div>

              {/* Examples of Host Challenges */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="rounded-2xl item-border border-white/10 bg-gradient-to-br from-white/5 to-transparent backdrop-blur-sm p-6 md:p-8"
              >
                <h3 className="text-xl md:text-2xl font-normal mb-4">
                  Examples of Host Challenges
                </h3>
                <p className="text-research-gray text-base md:text-lg mb-4">
                  BRespondents shared real scenarios:
                </p>
                <ul className="space-y-2 ml-6 mb-4">
                  <li className="text-research-gray text-base md:text-lg">
                    <span className="text-[#FD5B38] mr-2">•</span>
                    Difficulty covering operational costs while staying
                    competitive
                  </li>
                  <li className="text-research-gray text-base md:text-lg">
                    <span className="text-[#FD5B38] mr-2">•</span>
                    Uncertainty during low-demand periods
                  </li>
                  <li className="text-research-gray text-base md:text-lg">
                    <span className="text-[#FD5B38] mr-2">•</span>
                    Impact of guest feedback and property-specific conditions on
                    pricing decisions
                  </li>
                </ul>
                <p className="text-research-gray text-base md:text-lg">
                  These examples underline the need for holistic pricing tools
                  that integrate occupancy trends, market data, and guest
                  insights.
                </p>
              </motion.div>

              {/* Conclusion */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="rounded-2xl item-border border-white/10 bg-gradient-to-br from-white/5 to-transparent backdrop-blur-sm p-6 md:p-8"
              >
                <h3 className="text-2xl md:text-3xl font-normal mb-4">
                  Conclusion
                </h3>
                <p className="text-research-gray text-base md:text-lg mb-4">
                  The survey identified key challenges and opportunities related
                  to Airbnb pricing strategies. While hosts generally appreciate
                  existing tools, a significant number struggle with
                  understanding how automated pricing works and how well it
                  reflects real-world conditions. Hosts need tools that are:
                </p>
                <ul className="space-y-2 ml-6 mb-4">
                  <li className="text-research-gray text-base md:text-lg">
                    <span className="text-[#FD5B38] mr-2">•</span>
                    Transparent in pricing logic
                  </li>
                  <li className="text-research-gray text-base md:text-lg">
                    <span className="text-[#FD5B38] mr-2">•</span>
                    Adaptable to property-specific requirements
                  </li>
                  <li className="text-research-gray text-base md:text-lg">
                    <span className="text-[#FD5B38] mr-2">•</span>
                    Aligned with real-time market insights
                  </li>
                  <li className="text-research-gray text-base md:text-lg">
                    <span className="text-[#FD5B38] mr-2">•</span>
                    User-friendly for both new and experienced hosts
                  </li>
                </ul>
                <p className="text-research-gray text-base md:text-lg">
                  Enhancements such as competitor benchmarking, real-time market
                  analysis, and clear explanations of pricing recommendations
                  would significantly improve host confidence and support better
                  decision-making. These improvements would address current pain
                  points while strengthening Airbnb's ability to support its
                  diverse host community.
                </p>
              </motion.div>
            </div>
          </AnimatedSection>
        </div>
      </Section>

      {/* Interview (Check Question) */}
      <Section id="interview">
        <div className="max-w-7xl mx-auto px-4">
          <SectionHeader number="" title="Interview (Check Question)" />
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="py-8 sm:py-10"
          >
            <div className="py-7">
              <motion.p
                className="text-gray-200 text-lg sm:text-xl leading-relaxed"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.8 }}
              >
                To gain deeper insights into the challenges and strategies
                surrounding Airbnb pricing, a series of in-depth interviews were
                conducted with hosts managing various property types. These
                interviews aimed to uncover the qualitative factors influencing
                pricing decisions, explore the challenges hosts commonly face,
                and identify opportunities to improve pricing strategies and
                overall outcomes.
              </motion.p>

              <motion.p
                className="text-gray-200 text-lg sm:text-xl"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.8 }}
              >
                The interviews were structured to investigate core themes such
                as:
              </motion.p>

              <motion.ul
                className="py-3 pl-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7, duration: 0.8 }}
              >
                {[
                  "Pricing strategy decision-making",
                  "The impact of competition, seasonality, and guest feedback",
                  "Host-specific frustrations and areas for improvement",
                ].map((item, i) => (
                  <motion.li
                    key={i}
                    className="flex items-start gap-3 text-gray-200 text-lg sm:text-xl"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.8 + i * 0.1 }}
                  >
                    <span className="text-[#FD5B38] text-2xl leading-8">•</span>
                    {item}
                  </motion.li>
                ))}
              </motion.ul>

              <motion.p
                className="text-gray-200 text-lg sm:text-xl leading-relaxed"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.2, duration: 0.8 }}
              >
                A semi-structured interview format was used to maintain
                consistency across participants while allowing flexibility to
                explore unique experiences. This approach encouraged hosts to
                share rich, detailed accounts of their processes, challenges,
                and suggestions, leading to actionable insights and a
                comprehensive understanding of the complexities involved in
                pricing.
              </motion.p>
            </div>
          </motion.div>

          {/* /* Key Factors Section */}
          <ParallaxSection offset={40} className="mt-12">
            <div className="space-y-4">
              <h2 className="text-[#D4B896] text-2xl sm:text-3xl font-normal">
                Surveys | Key Factors Influence Pricing Decisions
              </h2>
              <p className="text-[#D4B896CC] text-base sm:text-lg">
                Participants: 21 Members
              </p>
            </div>

            <div className="p-8 sm:p-12 rounded-3xl item-border border-white/10 bg-white/5 mt-12">
              {/* First row of pie charts */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-12">
                <PieChartAdvanced
                  percentage={42}
                  label="LOCATION OF THE PROPERTY"
                  count="(9/21)"
                  delay={0}
                />
                <PieChartAdvanced
                  percentage={14}
                  label="PROPERTY TYPE AND AMENITIES"
                  count="(3/21)"
                  delay={0.2}
                />
                <PieChartAdvanced
                  percentage={16}
                  label="COMPETITOR PRICING"
                  count="(3/21)"
                  delay={0.4}
                />
              </div>

              {/* Second row of pie charts */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 max-w-4xl mx-auto">
                <PieChartAdvanced
                  percentage={24}
                  label="SEASONALITY/HIGH/LOW DEMAND PERIODS"
                  count="(5/21)"
                  delay={0.6}
                />
                <PieChartAdvanced
                  percentage={4}
                  label="GUEST RATINGS AND REVIEW"
                  count="(1/21)"
                  delay={0.8}
                />
              </div>
            </div>
          </ParallaxSection>

          {/* Strategies Section */}
          <ParallaxSection offset={40} className="mt-12">
            <div className="space-y-4">
              <h2 className="text-[#D4B896] text-2xl sm:text-3xl font-normal">
                Surveys | Strategies for Pricing Airbnb Listings
              </h2>
              <p className="text-[#D4B896CC] text-base sm:text-lg">
                Participants: 21 Members
              </p>
            </div>

            <div className="p-8 sm:p-12 rounded-3xl item-border border-white/10 bg-white/5 mt-12">
              <ProgressBar
                label="Based on occupancy rates"
                percentage={38.1}
                count="(9/21)"
                delay={0}
              />
              <ProgressBar
                label="Based on time of year/seasonality"
                percentage={28.8}
                count="(6/21)"
                delay={0.2}
              />
              <ProgressBar
                label="Based on guest reviews/ratings"
                percentage={19}
                count="(4/21)"
                delay={0.4}
              />
              <ProgressBar
                label="Based on a pricing tool (e.g. Airbnb's smart pricing"
                percentage={9.5}
                count="(2/21)"
                delay={0.6}
              />
            </div>
          </ParallaxSection>

          {/* Pricing Frequency Section */}
          <ParallaxSection offset={40} className="mt-12">
            <div className="space-y-4">
              <h2 className="text-[#D4B896] text-2xl sm:text-3xl font-normal">
                Surveys | Frequency of Pricing Adjustments for Listings
              </h2>
              <p className="text-[#D4B896CC] text-base sm:text-lg">
                Participants: 21 Members
              </p>
            </div>

            <div className="p-8 sm:p-12 rounded-3xl item-border border-white/10 bg-white/5 mt-12">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 sm:gap-16">
                {[
                  {
                    label: "During Peak Seasons/events",
                    percentage: 52,
                    count: "(11/21)",
                  },
                  { label: "Daily", percentage: 19, count: "(4/21)" },
                  { label: "Weekly", percentage: 19, count: "(4/21)" },
                  { label: "Rarely", percentage: 10, count: "(2/21)" },
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.6, delay: i * 0.15 }}
                    className="flex flex-col items-center gap-4"
                  >
                    <div className="relative w-48 h-48 sm:w-56 sm:h-56">
                      <svg
                        className="w-full h-full -rotate-90"
                        viewBox="0 0 224 224"
                      >
                        <circle
                          cx="112"
                          cy="112"
                          r="110"
                          fill="none"
                          stroke="rgba(255, 255, 255, 0.1)"
                          strokeWidth="2"
                        />
                        <motion.circle
                          cx="112"
                          cy="112"
                          r="110"
                          fill="none"
                          stroke="#D4B896"
                          strokeWidth="2"
                          strokeDasharray={`${
                            (item.percentage / 100) * 691.15
                          } 691.15`}
                          initial={{ strokeDashoffset: 691.15 }}
                          whileInView={{ strokeDashoffset: 0 }}
                          viewport={{ once: true }}
                          transition={{
                            duration: 1.2,
                            delay: i * 0.15 + 0.3,
                            ease: "easeOut",
                          }}
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <p className="text-white text-4xl sm:text-5xl font-normal">
                          {item.percentage}%
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2 text-center">
                      <p className="text-white text-base sm:text-lg">
                        {item.label}
                      </p>
                      <p className="text-[#D4B896] text-base">{item.count}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </ParallaxSection>

          <ParallaxSection offset={40} className="mt-12">
            <img
              src="/assets/imgs/case-studies/ipm_4.png"
              alt="Airbnb Pricing Survey Insights"
              className="w-full rounded-2xl item-border border-white/10 bg-white/5"
            />
          </ParallaxSection>

          {/* Participant Selection Section */}
          <ParallaxSection offset={35}>
            <motion.div className="mt-8 p-8 sm:p-10 rounded-2xl item-border border-white/10 bg-gradient-to-br from-white/5 to-transparent space-y-6">
              <h3 className="text-white text-2xl sm:text-3xl font-normal">
                Participant Selection & Recruitment
              </h3>

              <div className="space-y-6 text-gray-200 text-lg sm:text-xl leading-relaxed">
                <p>
                  Participants were selected through purposive sampling to
                  ensure a diverse mix of Airbnb hosts with varying levels of
                  experience and backgrounds. Recruitment was carried out
                  through:
                </p>

                <ul className="space-y-3 pl-6">
                  {[
                    "Online Airbnb communities and forums",
                    "Local Airbnb host groups",
                    "Personal professional networks",
                  ].map((item, i) => (
                    <motion.li
                      key={i}
                      className="flex items-start gap-3"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 }}
                    >
                      <span className="text-[#FD5B38] text-2xl leading-7">
                        •
                      </span>
                      {item}
                    </motion.li>
                  ))}
                </ul>

                <p>
                  This recruitment strategy ensured a broad representation of
                  host experiences and pricing perspectives.
                </p>
              </div>
            </motion.div>
          </ParallaxSection>

          {/* Participant Profile Section */}
          <ParallaxSection offset={35}>
            <motion.div className="mt-8 p-8 sm:p-10 rounded-2xl item-border border-[#FD5B3833] bg-gradient-to-br from-[#FD5B381A] via-[#FD5B380D] to-transparent space-y-6">
              <h3 className="text-white text-2xl sm:text-3xl font-normal">
                Participant Profile
              </h3>

              <div className="space-y-6 text-gray-100 text-lg sm:text-xl leading-relaxed">
                <p>
                  A total of six hosts took part in the interviews,
                  representing:
                </p>

                <ul className="space-y-4 pl-6">
                  {[
                    {
                      bold: "A range of experience levels:",
                      text: "from less than one year to over five years of hosting",
                    },
                    {
                      bold: "Different property types:",
                      text: "entire homes, private rooms, and shared spaces",
                    },
                    {
                      bold: "Varied geographic locations:",
                      text: "including high-demand urban areas (e.g., San Francisco) and suburban regions (e.g., Fairfax)",
                    },
                  ].map((item, i) => (
                    <motion.li
                      key={i}
                      className="flex items-start gap-3"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 }}
                    >
                      <span className="text-[#FD5B38] text-2xl leading-7">
                        •
                      </span>
                      <span>
                        <strong className="text-white font-bold">
                          {item.bold}
                        </strong>{" "}
                        {item.text}
                      </span>
                    </motion.li>
                  ))}
                </ul>

                <p>
                  This diversity allowed for a holistic understanding of the
                  pricing challenges faced across different markets and property
                  types.
                </p>
              </div>
            </motion.div>
          </ParallaxSection>

          {/* Interview Format Section */}
          <ParallaxSection offset={35} className="mt-8">
            <h3 className="text-white text-2xl sm:text-3xl font-normal">
              Interview Format
            </h3>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  title: "Format",
                  content: "Conducted virtually through video conferencing",
                },
                {
                  title: "Duration",
                  content: "Approximately 45 to 60 minutes each",
                },
                {
                  title: "Documentation",
                  content:
                    "Detailed notes were taken, and all sessions were recorded for accurate transcription and analysis",
                },
              ].map((card, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.6, delay: i * 0.15 }}
                  className="p-6 rounded-xl item-border border-white/10 bg-gradient-to-br from-white/5 to-transparent space-y-3"
                >
                  <h4 className="text-[#FD5B38] text-lg sm:text-xl">
                    {card.title}
                  </h4>
                  <p className="text-gray-200 text-base leading-relaxed">
                    {card.content}
                  </p>
                </motion.div>
              ))}
            </div>

            <motion.p
              className="my-8 text-gray-200 text-lg sm:text-xl leading-relaxed"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
            >
              The recordings and notes were used to perform a thorough thematic
              analysis, helping identify recurring patterns, themes, and unique
              insights that complemented the survey findings.
            </motion.p>
          </ParallaxSection>

          {/* Value of Interviews Section */}
          <ParallaxSection offset={35}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
              className="p-8 sm:p-10 rounded-2xl item-border border-white/10 bg-gradient-to-br from-white/5 to-transparent space-y-6"
            >
              <h3 className="text-white text-2xl sm:text-3xl font-normal">
                Value of the Interviews
              </h3>

              <div className="space-y-6 text-gray-200 text-lg sm:text-xl leading-relaxed">
                <p>
                  The interviews provided deeper context and nuance that
                  quantitative survey data could not fully capture. Hosts shared
                  personal experiences, decision-making processes, and obstacles
                  encountered when setting prices. These insights highlighted:
                </p>

                <ul className="space-y-3 pl-6">
                  {[
                    "The complexity of balancing competition, seasonality, and guest expectations",
                    "The need for more transparent and adaptive pricing tools",
                    "Opportunities to enhance host support through better data, automation, and guidance",
                  ].map((item, i) => (
                    <motion.li
                      key={i}
                      className="flex items-start gap-3"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 }}
                    >
                      <span className="text-[#FD5B38] text-2xl leading-7">
                        •
                      </span>
                      {item}
                    </motion.li>
                  ))}
                </ul>

                <p>
                  Overall, the interviews greatly enriched the understanding of
                  Airbnb's pricing dynamics, contributing to more informed
                  recommendations for improving pricing tools and host
                  experience.
                </p>
              </div>
            </motion.div>
          </ParallaxSection>

          {/* Key Interview Questions Section */}
          <ParallaxSection offset={35} className="space-y-8">
            <h3 className="text-white text-2xl sm:text-3xl font-normal my-8">
              Key Interview Questions
            </h3>

            <div className="p-8 sm:p-10 rounded-2xl item-border border-white/10 bg-gradient-to-br from-white/5 to-transparent space-y-6">
              <p className="text-gray-200 text-lg sm:text-xl">
                The interviews explored the following questions:
              </p>

              <div className="space-y-4">
                {[
                  "How long have you been an Airbnb host?",
                  "What type of property do you typically list (e.g., entire home, private room)?",
                  "How do you usually decide on the price for your listing(s)?",
                  "What key factors influence your pricing decisions (e.g., location, room type, guest reviews)?",
                  "What challenges do you face when setting the right price for your listing(s)?",
                  "How do you manage price competition with other Airbnb hosts in your area?",
                  "Have you used any tools or services to help set your listing prices (e.g., Airbnb's Smart Pricing, third-party tools)?",
                  "How much do guest reviews and ratings affect the price you set?",
                  "Do you consider neighborhood demand or proximity to tourist attractions when determining your prices?",
                ].map((question, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ duration: 0.5, delay: i * 0.05 }}
                    className="flex items-start gap-4 p-4 sm:p-5 rounded-xl bg-black/20"
                  >
                    <span className="text-[#FD5B38] text-lg mt-0.5">•</span>
                    <p className="text-gray-200 text-base sm:text-lg flex-1">
                      {question}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </ParallaxSection>

          {/* Closing Statement Section */}
          <ParallaxSection offset={30}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
              className="mt-8 p-8 sm:p-10 rounded-2xl item-border border-white/10 bg-gradient-to-br from-white/5 to-transparent"
            >
              <p className="text-gray-200 text-lg sm:text-xl leading-relaxed">
                The semi-structured interview format proved highly effective,
                allowing participants to share detailed personal experiences and
                practical insights. For example, one host explained how a sudden
                increase in local competition sharply reduced their revenue
                despite consistently high guest ratings. Several hosts also
                voiced concerns about automated pricing tools, specifically
                regarding their lack of transparency and limited user control.
                These stories highlighted both individual struggles and systemic
                challenges, indicating potential areas for innovation. As
                interviews progressed, recurring concerns began to surface,
                providing the basis for deeper thematic analysis.
              </p>
            </motion.div>
          </ParallaxSection>

          {/* Top 6 Insights Section */}
          <ParallaxSection offset={40} className="space-y-12">
            <TopInsights />
          </ParallaxSection>

          {/* Journey Mapping Sections */}
          <ParallaxSection offset={40} className="space-y-16">
            <div className="space-y-4 text-center">
              <h2 className="text-white text-2xl sm:text-3xl font-normal">
                Journey Mapping
              </h2>
              <p className="text-gray-300 text-base sm:text-lg">
                Sarah's Journey
              </p>
            </div>

            <JourneyMap
              stages={[
                "Need",
                "Search",
                "Find",
                "Set Up",
                "Manage",
                "Evaluate",
              ]}
              experiences={[
                "Looks for simple pricing solutions",
                "Feels lost in too many options",
                "Relieved to find a guided tool",
                "Confused by unclear setup instructions",
                "Struggles to balance demand fluctuations",
                "Doubts if changes improved performance",
              ]}
              expectations={[
                "Easy pricing setup without technical complexities",
                "Simple, clear information on seasonal pricing",
                "Step-by-step setup with seasonal tips",
                "Guided setup for attractive pricing",
                "Customer support for setup questions",
                "Increased occupancy and ratings",
              ]}
              painPoints={[
                "Uncertain about choosing the right tool",
                "Overwhelmed by options and complexity",
                "Doubts if tool fits her needs",
                "Without more guidance, setup seems pretty complicated",
                "Lack of confidence in adjusting for demand",
                "Nervous if changes boost occupancy",
              ]}
              emotions={[
                { label: "New start", sentiment: "happy", position: 15 },
                { label: "Too many", sentiment: "sad", position: 50 },
                { label: "Simple option", sentiment: "happy", position: 25 },
                {
                  label: "Setup struggles",
                  sentiment: "neutral",
                  position: 35,
                },
                { label: "Manual overrides", sentiment: "sad", position: 55 },
                { label: "Long term doubts", sentiment: "sad", position: 60 },
              ]}
            />
          </ParallaxSection>

          <ParallaxSection offset={40} className="space-y-16">
            <div className="space-y-4 text-center">
              <p className="text-gray-300 text-base sm:text-lg">
                Mark's Journey
              </p>
            </div>

            <JourneyMap
              stages={[
                "Need",
                "Search",
                "Find",
                "Set Up",
                "Manage",
                "Evaluate",
              ]}
              experiences={[
                "Looks for better pricing strategies",
                "Struggles with too many options",
                "Feels confident after finding a tool",
                "Finds setup lengthy and time-consuming",
                "Faces challenges with constant adjustments",
                "Doubtful if tool meets expectations",
              ]}
              expectations={[
                "Automated, reliable pricing adjustments",
                "Simple, yet powerful analytics tool",
                "Customization for peak and off-peak rates",
                "Multi-property installation in a quick and simple way",
                "Occupancy fluctuation handling support",
                "Improvement in revenue and constant occupancy",
              ]}
              painPoints={[
                "Manual tweaks that are time consuming",
                "Overwhelmed by too many features",
                "Concerns over tool might not satisfy needs",
                "Setup takes time due to customization",
                "Demand spikes are not met by Tool",
                "Needs confident tool can work independently",
              ]}
              emotions={[
                { label: "Revenue growth", sentiment: "happy", position: 15 },
                { label: "Tool overload", sentiment: "neutral", position: 42 },
                { label: "Right fit", sentiment: "happy", position: 25 },
                { label: "Lengthy setup", sentiment: "neutral", position: 38 },
                {
                  label: "Market fluctuations",
                  sentiment: "sad",
                  position: 58,
                },
                {
                  label: "Effectiveness doubts",
                  sentiment: "sad",
                  position: 62,
                },
              ]}
            />
          </ParallaxSection>
        </div>
      </Section>

      {/* TKDID section */}
      <TKDID />

      <div className="min-h-screen overflow-hidden mx-auto max-w-7xl">
        {/* Hero Section */}
        <ParallaxSection speed={0.3}>
          <div className="pt-20 pb-12 md:pt-32 md:pb-16 px-4">
            <SectionHeader title="Design Process" />
          </div>
        </ParallaxSection>

        {/* Storyboarding Section */}
        <ParallaxSection speed={0.4}>
          <section className="py-12 md:py-16">
            <AnimatedSection>
              <GlassCard>
                <h3 className="text-[24px] leading-[30px] md:text-h2 font-normal text-white mb-8">
                  Storyboarding
                </h3>

                <div className="space-y-6">
                  <p className="text-[#D1D5DC]">
                    To initiate the design process, I started with a Crazy 8
                    exercise to generate a wide range of ideas focused on Airbnb
                    hosts' major pain points—dynamic pricing, ease of setup, and
                    transparency. This rapid ideation helped surface diverse
                    concepts such as enhanced onboarding, customizable pricing
                    flows, and competitive comparison tools.
                  </p>

                  <p className="text-[#D1D5DC]">
                    Following the Crazy 8 session, I developed a detailed
                    storyboard that visualized the host's journey inside the
                    Metrix app. The storyboard mapped key interactions from
                    discovering the platform to exploring and applying
                    intelligent pricing recommendations. Each frame illustrated
                    important stages such as account creation, onboarding, data
                    entry, and pricing exploration.
                  </p>

                  <p className="text-[#D1D5DC]">
                    This visual narrative played a crucial role in identifying
                    usability gaps, strengthening workflow clarity, and guiding
                    the creation of low-fidelity wireframes. It ensured that the
                    design direction stayed aligned with user research insights
                    and the real behaviors and goals of Airbnb hosts.
                  </p>
                </div>

                <motion.img
                  src="/assets/imgs/case-studies/ipm_28.png"
                  alt="Storyboarding Process"
                  className="mt-8 w-full rounded-lg"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                />
              </GlassCard>
            </AnimatedSection>
          </section>
        </ParallaxSection>

        {/* User Flow Section */}
        <ParallaxSection speed={0.5}>
          <section className="py-12 md:py-16">
            <AnimatedSection>
              <div className="space-y-8">
                <h3 className="text-[24px] leading-[30px] md:text-h2 font-normal text-white px-4">
                  User Flow
                </h3>

                <p className="text-body-sm md:text-[#D1D5DC] max-w-4xl px-4">
                  To establish a clear structure for the Metrix app, I created a
                  comprehensive user flow diagram focusing on two major
                  processes:
                </p>

                <div className="space-y-6">
                  <GlassCard delay={0.1}>
                    <h4 className="text-h4 font-normal text-[#FD5B38] mb-4">
                      Login/Signup Flow
                    </h4>
                    <p className="text-[#D1D5DC]">
                      This flow outlines how users begin their journey. New
                      users can register using basic information—name, email,
                      password—or sign up quickly via Google or Facebook. Email
                      verification adds a security layer before access is
                      granted.
                    </p>
                  </GlassCard>

                  <div className="px-4">
                    <motion.img
                      src="/assets/imgs/case-studies/ipm_27.png"
                      alt="Login Signup Flow"
                      className="w-full rounded-lg"
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8 }}
                    />
                  </div>

                  <GlassCard delay={0.2}>
                    <h4 className="text-h4 font-normal text-[#FD5B38] mb-4">
                      Customer Interaction Flow
                    </h4>
                    <p className="text-[#D1D5DC]">
                      This is the core flow that guides users from onboarding to
                      receiving pricing suggestions. After logging in, users see
                      a short feature tour and then input key property
                      information such as location, room type, and amenities.
                      Based on this data, the app generates smart pricing
                      suggestions that hosts can adjust, accept, or explore
                      further. Contextual notifications assist users when
                      pricing updates or market changes require attention.
                    </p>
                  </GlassCard>

                  <div className="px-4">
                    <motion.img
                      src="/assets/imgs/case-studies/ipm_26.png"
                      alt="Customer Interaction Flow"
                      className="w-full rounded-lg"
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8 }}
                    />
                  </div>

                  <CoralCard delay={0.3}>
                    <p className="text-body-light">
                      This flow was intentionally designed to be simple and
                      helpful, especially for first-time hosts or those new to
                      pricing tools, ensuring confidence throughout the process.
                    </p>
                  </CoralCard>
                </div>
              </div>
            </AnimatedSection>
          </section>
        </ParallaxSection>

        {/* Low-Fidelity Wireframes Section */}
        <ParallaxSection speed={0.4}>
          <section className="py-12 md:py-16">
            <AnimatedSection>
              <GlassCard>
                <h3 className="text-[24px] leading-[30px] md:text-h2 font-normal text-white mb-8">
                  Low-Fidelity Wireframes
                </h3>

                <div className="space-y-6">
                  <p className="text-[#D1D5DC]">
                    I created low-fidelity wireframes to define the structure
                    and main features of the Metrix app. These grayscale layouts
                    focused on essentials such as login/signup, onboarding,
                    property setup, pricing adjustments, and key reporting
                    features.
                  </p>

                  <p className="text-[#D1D5DC]">
                    The wireframes supported early visualization of the app's
                    information architecture and user flow without getting
                    distracted by visual styling. This allowed rapid iteration
                    and easier modifications based on initial usability
                    feedback.
                  </p>

                  <p className="text-[#D1D5DC]">
                    The onboarding sequence begins after downloading the app,
                    guiding users through account creation. After signing up,
                    users add their property details—property type, rooms,
                    amenities—which help personalize pricing recommendations.
                  </p>

                  <p className="text-[#D1D5DC]">
                    The dashboard gives an overview of performance and allows
                    recalculation or adjustment of prices. On the pricing
                    screen, users can explore market trends, accept suggestions,
                    or manually refine nightly rates. The final pricing report
                    screen displays multi-property data with options to save,
                    download, or share.
                  </p>

                  <p className="text-[#D1D5DC]">
                    The home screen serves as the main hub featuring tutorials,
                    partner integrations, and bottom navigation for quick access
                    to saved properties, pricing tools, notifications, and user
                    profile. Notifications highlight local trends, events, and
                    seasonal pricing opportunities.
                  </p>

                  <p className="text-[#D1D5DC]">
                    Additional pages like Saved Properties, Today's Trends, and
                    History help users manage listings, view market dynamics,
                    and track operational costs such as utilities, bills, and
                    maintenance.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-8 mt-8">
                  <motion.img
                    src="/assets/imgs/case-studies/ipm_25.png"
                    alt="Low-Fidelity Wireframes Set 1"
                    className="w-full rounded-lg"
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                  />
                  <motion.img
                    src="/assets/imgs/case-studies/ipm_24.png"
                    alt="Low-Fidelity Wireframes Set 2"
                    className="w-full rounded-lg"
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.1 }}
                  />
                  <motion.img
                    src="/assets/imgs/case-studies/ipm_23.png"
                    alt="Low-Fidelity Wireframes Set 3"
                    className="w-full rounded-lg"
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                  />
                </div>
              </GlassCard>
            </AnimatedSection>
          </section>
        </ParallaxSection>

        {/* Usability Testing I Section */}
        <ParallaxSection speed={0.5}>
          <section className="py-12 md:py-16 px-4">
            <AnimatedSection>
              <div className="space-y-8">
                <h3 className="text-[24px] leading-[30px] md:text-h2 font-normal text-white">
                  Usability Testing I
                </h3>

                <div className="space-y-6">
                  <p className="text-body-new">
                    For the first usability testing round, I conducted moderated
                    remote sessions with 5 participants on Microsoft Teams using
                    a low-fidelity Figma prototype. Each session lasted 35–40
                    minutes and evaluated the ease of use, clarity, and
                    effectiveness of the Metrix app.
                  </p>

                  <p className="text-body-new">
                    Participants were 25–40 years old, selected based on their
                    hosting experience and familiarity with pricing tools. They
                    followed a contextual scenario simulating real-world
                    struggles with fluctuating bookings and the desire for
                    smarter pricing strategies.
                  </p>
                </div>

                <CoralCard delay={0.1}>
                  <h4 className="text-[20px] leading-[26px] md:text-h3 font-normal text-white mb-4 !text-white">
                    Scenario Provided
                  </h4>
                  <p className="text-body-light">
                    As an Airbnb host struggling with inconsistent revenue,
                    fluctuating demand, and event-driven price changes, you
                    explore Metrix to simplify pricing and improve earnings. You
                    sign up and test the app's features to understand how it can
                    support your pricing strategy.
                  </p>
                </CoralCard>

                <GlassCard delay={0.2}>
                  <h4 className="text-[20px] leading-[26px] md:text-h3 font-normal text-white mb-6 !text-white">
                    Tasks Assigned
                  </h4>

                  <div className="space-y-3">
                    <div className="flex gap-3">
                      <span className="text-[#FD5B38] text-2xl font-normal">
                        1.
                      </span>
                      <div>
                        <span className="text-white font-semibold">
                          Onboarding:
                        </span>
                        <span className="text-body-sm md:text-body text-[#D1D5DC] ml-2">
                          Complete the signup process.
                        </span>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <span className="text-[#FD5B38] text-2xl font-normal">
                        2.
                      </span>
                      <div>
                        <span className="text-white font-semibold">
                          Navigation:
                        </span>
                        <span className="text-body-sm md:text-body text-[#D1D5DC] ml-2">
                          Explore the pricing model interface.
                        </span>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <span className="text-[#FD5B38] text-2xl font-normal">
                        3.
                      </span>
                      <div>
                        <span className="text-white font-semibold">
                          Set a Price:
                        </span>
                        <span className="text-body-sm md:text-body text-[#D1D5DC] ml-2">
                          Adjust nightly rates using market data.
                        </span>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <span className="text-[#FD5B38] text-2xl font-normal">
                        4.
                      </span>
                      <div>
                        <span className="text-white font-semibold">
                          Dynamic Pricing Simulation:
                        </span>
                        <span className="text-body-sm md:text-body text-[#D1D5DC] ml-2">
                          Apply dynamic pricing and observe automated updates.
                        </span>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <span className="text-[#FD5B38] text-2xl font-normal">
                        5.
                      </span>
                      <div>
                        <span className="text-white font-semibold">
                          Customization:
                        </span>
                        <span className="text-body-sm md:text-body text-[#D1D5DC] ml-2">
                          Modify pricing based on guest preferences, events, or
                          seasonal trends.
                        </span>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <span className="text-[#FD5B38] text-2xl font-normal">
                        6.
                      </span>
                      <div>
                        <span className="text-white font-semibold">
                          Review Insights:
                        </span>
                        <span className="text-body-sm md:text-body text-[#D1D5DC] ml-2">
                          Interpret pricing recommendations and market insights.
                        </span>
                      </div>
                    </div>
                  </div>
                </GlassCard>

                <div className="space-y-4">
                  <p className="text-body-new">
                    Participants were encouraged to think aloud while performing
                    tasks. They responded to questions such as:
                  </p>

                  <div className="pl-6 space-y-2">
                    <p className="bullet-coral text-body-new">
                      Describe your overall experience.
                    </p>
                    <p className="bullet-coral text-body-new">
                      What felt most intuitive or useful?
                    </p>
                    <p className="bullet-coral text-body-new">
                      What challenges did you face?
                    </p>
                    <p className="bullet-coral text-body-new">
                      Did you trust the pricing recommendations? Why or why not?
                    </p>
                    <p className="bullet-coral text-body-new">
                      What features were missing?
                    </p>
                    <p className="bullet-coral text-body-new">
                      Would you continue using Metrix?
                    </p>
                  </div>
                </div>

                <GlassCard delay={0.3}>
                  <h4 className="text-[24px] leading-[30px] md:text-h2 font-normal text-white mb-6">
                    Analysis and Report of Usability Test I (Low-Fidelity
                    Wireframes)
                  </h4>

                  <div className="space-y-6">
                    <p className="text-body-new">
                      I used a qualitative analysis approach based on recorded
                      sessions and transcription reviews. By observing behaviors
                      and navigation patterns, I extracted insights and
                      organized them via affinity mapping. Digital sticky notes
                      helped categorize pain points, recurring themes, and user
                      expectations.
                    </p>

                    <p className="text-body-new">
                      This process revealed usability concerns, mental models,
                      and trust indicators that guided improvements for the next
                      iteration. Insights were prioritized by severity to shape
                      design decisions related to visual clarity, pricing
                      transparency, and general usability.
                    </p>
                  </div>

                  <motion.img
                    src="/assets/imgs/case-studies/ipm_22.png"
                    alt="Affinity Mapping"
                    className="mt-8 w-full rounded-lg"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                  />
                </GlassCard>
              </div>
            </AnimatedSection>
          </section>
        </ParallaxSection>

        {/* Key Insights Section */}
        <ParallaxSection speed={0.4}>
          <section className="py-12 md:py-16 px-4">
            <AnimatedSection>
              <div className="space-y-8">
                <h3 className="text-[24px] leading-[30px] md:text-h2 font-normal text-white">
                  Key Insights from Usability Test I
                </h3>

                <div className="space-y-4">
                  <GlassCard delay={0}>
                    <p className="text-body-new">
                      • 3/5 participants found the pricing logic unclear; they
                      wanted transparency on how prices were calculated.
                    </p>
                  </GlassCard>

                  <GlassCard delay={0.05}>
                    <p className="text-body-new">
                      • 3/5 found navigation smooth but requested a visible
                      progress tracker for multi-step tasks.
                    </p>
                  </GlassCard>

                  <GlassCard delay={0.1}>
                    <p className="text-body-new">
                      • 4/5 wanted manual expense entry with clear category
                      separation.
                    </p>
                  </GlassCard>

                  <GlassCard delay={0.15}>
                    <p className="text-body-new">
                      • All 5 valued dynamic pricing but requested more
                      actionable market data and event insights.
                    </p>
                  </GlassCard>

                  <GlassCard delay={0.2}>
                    <p className="text-body-new">
                      • 4/5 found property setup restrictive due to missing
                      property types and clunky dropdowns; they wanted a toggle
                      between dynamic and manual pricing.
                    </p>
                  </GlassCard>

                  <GlassCard delay={0.25}>
                    <p className="text-body-new">
                      • 3/5 emphasized the need for better onboarding guidance
                      and quicker access to help features.
                    </p>
                  </GlassCard>
                </div>
              </div>
            </AnimatedSection>
          </section>
        </ParallaxSection>

        {/* Next Steps Section */}
        <ParallaxSection speed={0.5}>
          <section className="py-12 md:py-16 px-4">
            <AnimatedSection>
              <CoralCard>
                <h3 className="text-[24px] leading-[30px] md:text-h2 font-normal text-white mb-8 !text-white">
                  Next Steps Based on Insights
                </h3>

                <div className="space-y-3">
                  <div className="flex gap-3">
                    <span className="text-[#FD5B38] text-2xl font-normal">
                      1.
                    </span>
                    <p className="text-body-light">
                      Add a transparent pricing breakdown panel showing base
                      rate, service charges, and seasonal adjustments.
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <span className="text-[#FD5B38] text-2xl font-normal">
                      2.
                    </span>
                    <p className="text-body-light">
                      Introduce a manual "Add Expense" feature with categories
                      and a filterable dashboard.
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <span className="text-[#FD5B38] text-2xl font-normal">
                      3.
                    </span>
                    <p className="text-body-light">
                      Integrate seasonal event calendars and competitor pricing
                      alerts for real-time rate adjustments.
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <span className="text-[#FD5B38] text-2xl font-normal">
                      4.
                    </span>
                    <p className="text-body-light">
                      Add a progress tracker for onboarding, property setup, and
                      pricing steps, and improve navigation labels and grouping.
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <span className="text-[#FD5B38] text-2xl font-normal">
                      5.
                    </span>
                    <p className="text-body-light">
                      Enhance discoverability by replacing dropdowns with
                      tile-based selectors and adding toggles for dynamic vs.
                      manual pricing.
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <span className="text-[#FD5B38] text-2xl font-normal">
                      6.
                    </span>
                    <p className="text-body-light">
                      Offer a guided onboarding tutorial with contextual pop-ups
                      or walkthrough videos for new users.
                    </p>
                  </div>
                </div>
              </CoralCard>
            </AnimatedSection>
          </section>
        </ParallaxSection>

        {/* Iteration Screenshots */}
        <ParallaxSection speed={0.4}>
          <section className="py-12 md:py-16 px-4">
            <AnimatedSection>
              <div className="space-y-8">
                <h3 className="text-[24px] leading-[30px] md:text-h2 font-normal text-white">
                  Key Insights from Usability Test I
                </h3>

                <div className="grid grid-cols-1 gap-8">
                  <motion.img
                    src="/assets/imgs/case-studies/ipm_21.png"
                    alt="Before/After Iteration 1"
                    className="w-full rounded-lg"
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                  />
                  <motion.img
                    src="/assets/imgs/case-studies/ipm_20.png"
                    alt="Before/After Iteration 2"
                    className="w-full rounded-lg"
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.1 }}
                  />
                  <motion.img
                    src="/assets/imgs/case-studies/ipm_19.png"
                    alt="Before/After Iteration 3"
                    className="w-full rounded-lg"
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                  />
                </div>
              </div>
            </AnimatedSection>
          </section>
        </ParallaxSection>

        {/* Mid-Fidelity Wireframes */}
        <ParallaxSection speed={0.5}>
          <section className="py-12 md:py-16 px-4">
            <AnimatedSection>
              <div className="space-y-8">
                <h3 className="text-[24px] leading-[30px] md:text-h2 font-normal text-white">
                  Mid-Fidelity Wireframes
                </h3>

                <div className="grid grid-cols-1 gap-8">
                  <motion.img
                    src="/assets/imgs/case-studies/ipm_18.png"
                    alt="Mid-Fidelity Wireframes Set 1"
                    className="w-full rounded-lg"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                  />
                  <motion.img
                    src="/assets/imgs/case-studies/ipm_17.png"
                    alt="Mid-Fidelity Wireframes Set 2"
                    className="w-full rounded-lg"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.1 }}
                  />
                </div>
              </div>
            </AnimatedSection>
          </section>
        </ParallaxSection>

        {/* Usability Testing II Section */}
        <ParallaxSection speed={0.4}>
          <section className="py-12 md:py-16 px-4">
            <AnimatedSection>
              <div className="space-y-8">
                <h3 className="text-[24px] leading-[30px] md:text-h2 font-normal text-white">
                  Usability Testing II (Mid-Fidelity Wireframes)
                </h3>

                <p className="text-body-new">
                  For the second round of usability testing on Metrix's
                  mid-fidelity wireframes, I ran moderated remote sessions with
                  4 participants over Microsoft Teams. Each session lasted 30–40
                  minutes. Participants (aged 25–45) were active Airbnb hosts
                  familiar with basic pricing strategies. They used a Figma
                  prototype link and shared their screens while completing a
                  consistent set of tasks:
                </p>

                <GlassCard delay={0.1}>
                  <h4 className="text-[20px] leading-[26px] md:text-h3 font-normal text-white mb-6 !text-white">
                    Tasks (same as Round 1):
                  </h4>

                  <div className="space-y-3">
                    <div className="flex gap-3">
                      <span className="text-[#FD5B38] text-2xl font-normal">
                        1.
                      </span>
                      <div>
                        <span className="text-white font-semibold">
                          Onboarding:
                        </span>
                        <span className="text-body-sm md:text-body text-[#D1D5DC] ml-2">
                          follow instructions and sign up on Metrix.
                        </span>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <span className="text-[#FD5B38] text-2xl font-normal">
                        2.
                      </span>
                      <div>
                        <span className="text-white font-semibold">
                          Navigation:
                        </span>
                        <span className="text-body-sm md:text-body text-[#D1D5DC] ml-2">
                          open the pricing model interface and explore features.
                        </span>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <span className="text-[#FD5B38] text-2xl font-normal">
                        3.
                      </span>
                      <div>
                        <span className="text-white font-semibold">
                          Setting a price:
                        </span>
                        <span className="text-body-sm md:text-body text-[#D1D5DC] ml-2">
                          adjust a nightly rate using market data.
                        </span>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <span className="text-[#FD5B38] text-2xl font-normal">
                        4.
                      </span>
                      <div>
                        <span className="text-white font-semibold">
                          Dynamic pricing simulation:
                        </span>
                        <span className="text-body-sm md:text-body text-[#D1D5DC] ml-2">
                          apply dynamic pricing and observe automatic
                          adjustments.
                        </span>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <span className="text-[#FD5B38] text-2xl font-normal">
                        5.
                      </span>
                      <div>
                        <span className="text-white font-semibold">
                          Customization:
                        </span>
                        <span className="text-body-sm md:text-body text-[#D1D5DC] ml-2">
                          modify pricing for guest preferences, events, and
                          seasonality.
                        </span>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <span className="text-[#FD5B38] text-2xl font-normal">
                        6.
                      </span>
                      <div>
                        <span className="text-white font-semibold">
                          Review insights:
                        </span>
                        <span className="text-body-sm md:text-body text-[#D1D5DC] ml-2">
                          access pricing recommendations and interpret market
                          data.
                        </span>
                      </div>
                    </div>
                  </div>
                </GlassCard>

                <motion.img
                  src="/assets/imgs/case-studies/ipm_16.png"
                  alt="Usability Testing Session"
                  className="w-full rounded-lg"
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                />
              </div>
            </AnimatedSection>
          </section>
        </ParallaxSection>

        {/* Key Findings Round 2 */}
        <ParallaxSection speed={0.5}>
          <section className="py-12 md:py-16 px-4">
            <AnimatedSection>
              <div className="space-y-8">
                <h3 className="text-[24px] leading-[30px] md:text-h2 font-normal text-white">
                  Key Findings (Round 2)
                </h3>

                <div className="space-y-4">
                  <GlassCard delay={0}>
                    <p className="text-body-new">
                      • 2 / 4 participants found Smart Pricing useful but were
                      confused by the "Recalculate" button's purpose.
                    </p>
                  </GlassCard>

                  <GlassCard delay={0.05}>
                    <p className="text-body-new">
                      • 3 / 4 participants said icons were unclear and needed
                      supporting text labels.
                    </p>
                  </GlassCard>

                  <GlassCard delay={0.1}>
                    <p className="text-body-new">
                      • 3 / 4 participants strongly requested a manual expense
                      entry feature with the ability to upload
                      receipts/documents for better record-keeping.
                    </p>
                  </GlassCard>

                  <GlassCard delay={0.15}>
                    <p className="text-body-new">
                      • All participants appreciated the inclusion of seasonal
                      and local trends to inform pricing.
                    </p>
                  </GlassCard>

                  <GlassCard delay={0.2}>
                    <p className="text-body-new">
                      • 4 / 4 participants emphasized the need for a more
                      detailed and customizable property setup (missing image
                      uploads and limited amenity options).
                    </p>
                  </GlassCard>

                  <GlassCard delay={0.25}>
                    <p className="text-body-new">
                      • 2 / 4 participants wanted visible confirmation after
                      actions such as email verification or report downloads.
                    </p>
                  </GlassCard>
                </div>
              </div>
            </AnimatedSection>
          </section>
        </ParallaxSection>

        {/* Actionable Next Steps Round 2 */}
        <ParallaxSection speed={0.4}>
          <section className="py-12 md:py-16 px-4">
            <AnimatedSection>
              <CoralCard>
                <h3 className="text-[24px] leading-[30px] md:text-h2 font-normal text-white mb-8 !text-white">
                  Actionable Next Steps (informed by Round 2)
                </h3>

                <div className="space-y-3">
                  <div className="flex gap-3">
                    <span className="text-[#FD5B38] text-2xl font-normal">
                      1.
                    </span>
                    <p className="text-body-light">
                      Replace "Recalculate" label with "Edit" to remove
                      confusion.
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <span className="text-[#FD5B38] text-2xl font-normal">
                      2.
                    </span>
                    <p className="text-body-light">
                      Allow property image uploads during property setup.
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <span className="text-[#FD5B38] text-2xl font-normal">
                      3.
                    </span>
                    <p className="text-body-light">
                      Add receipt/document upload capability in the expense
                      input section.
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <span className="text-[#FD5B38] text-2xl font-normal">
                      4.
                    </span>
                    <p className="text-body-light">
                      Show a visual confirmation (e.g., download animation /
                      success banner) after report downloads.
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <span className="text-[#FD5B38] text-2xl font-normal">
                      5.
                    </span>
                    <p className="text-body-light">
                      Add descriptive text labels beneath navigation icons.
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <span className="text-[#FD5B38] text-2xl font-normal">
                      6.
                    </span>
                    <p className="text-body-light">
                      Expand amenity selection (parking, laundry, Wi-Fi, etc.).
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <span className="text-[#FD5B38] text-2xl font-normal">
                      7.
                    </span>
                    <p className="text-body-light">
                      Display a toast/banner on successful email verification.
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <span className="text-[#FD5B38] text-2xl font-normal">
                      8.
                    </span>
                    <p className="text-body-light">
                      Integrate a calendar view of local events and a 3-month
                      pricing trend graph to support timely pricing decisions.
                    </p>
                  </div>
                </div>
              </CoralCard>
            </AnimatedSection>
          </section>
        </ParallaxSection>

        {/* Style Guide Section */}
        <ParallaxSection speed={0.5}>
          <section className="py-12 md:py-16 px-4">
            <AnimatedSection>
              <GlassCard>
                <h3 className="text-[24px] leading-[30px] md:text-h2 font-normal text-white mb-8">
                  Style Guide (Mid → High Fidelity Handoff)
                </h3>

                <p className="text-body-sm md:text-body text-[#D1D5DC] mb-8">
                  Before moving to high-fidelity prototypes, I created a style
                  guide to ensure visual consistency, clarity, and
                  accessibility.
                </p>

                <div className="space-y-8">
                  <div className="bg-black/20 rounded-2xl p-6 space-y-4">
                    <h4 className="text-h4 font-normal text-[#FD5B38]">
                      Visual & Color:
                    </h4>
                    <div className="space-y-2 pl-6">
                      <p className="bullet-coral text-body-sm">
                        Primary CTA: Yellow #FFB200 (buttons, key CTAs).
                      </p>
                      <p className="bullet-coral text-body-sm">
                        Accent: Coral Red #F27E63 (warnings/highlights).
                      </p>
                      <p className="bullet-coral text-body-sm">
                        Background: Cream #FFF8EE.
                      </p>
                      <p className="bullet-coral text-body-sm">
                        Text: Dark Charcoal #2D2C33 (primary), Grey #6C6A74
                        (secondary).
                      </p>
                      <p className="bullet-coral text-body-sm">
                        All colors tested against WCAG accessibility standards.
                      </p>
                    </div>
                  </div>

                  <div className="bg-black/20 rounded-2xl p-6 space-y-4">
                    <h4 className="text-h4 font-normal text-[#FD5B38]">
                      Typography: (rounded modern sans-serif)
                    </h4>
                    <div className="space-y-2 pl-6">
                      <p className="bullet-coral text-body-sm">
                        H1 — Bold, 24pt (page titles)
                      </p>
                      <p className="bullet-coral text-body-sm">
                        H2 — Semi-bold, 20pt (section headings)
                      </p>
                      <p className="bullet-coral text-body-sm">
                        Body — Regular, 14pt (descriptions, hints)
                      </p>
                      <p className="bullet-coral text-body-sm">
                        Button — Medium, 16pt (CTA labels)
                      </p>
                    </div>
                  </div>

                  <div className="bg-black/20 rounded-2xl p-6 space-y-4">
                    <h4 className="text-h4 font-normal text-[#FD5B38]">
                      Buttons & Interactions:
                    </h4>
                    <div className="space-y-2 pl-6">
                      <p className="bullet-coral text-body-sm">
                        Primary buttons: filled yellow background + bold text.
                      </p>
                      <p className="bullet-coral text-body-sm">
                        Secondary actions: underlined text.
                      </p>
                      <p className="bullet-coral text-body-sm">
                        Subtle hover states: shadows or darkening for feedback.
                      </p>
                    </div>
                  </div>

                  <div className="bg-black/20 rounded-2xl p-6 space-y-4">
                    <h4 className="text-h4 font-normal text-[#FD5B38]">
                      Layout & Grid:
                    </h4>
                    <div className="space-y-2 pl-6">
                      <p className="bullet-coral text-body-sm">
                        12-column layout for mobile, margins 16px, gutters 8px.
                      </p>
                      <p className="bullet-coral text-body-sm">
                        Responsive column widths; consistent spacing for
                        polished UI.
                      </p>
                    </div>
                  </div>
                </div>

                <CoralCard className="mt-8">
                  <p className="text-body-light">
                    This system supports readability, accessibility, brand
                    coherence, and a smooth transition to high-fidelity designs.
                  </p>
                </CoralCard>
              </GlassCard>
            </AnimatedSection>
          </section>
        </ParallaxSection>

        {/* Iteration and Refinement */}
        <ParallaxSection speed={0.4}>
          <section className="py-12 md:py-16 px-4">
            <AnimatedSection>
              <div className="space-y-8">
                <h3 className="text-[24px] leading-[30px] md:text-h2 font-normal text-white">
                  Iteration and Refinement
                </h3>

                <div className="grid grid-cols-1 gap-8">
                  {[
                    "/assets/imgs/case-studies/ipm_11.png",
                    "/assets/imgs/case-studies/ipm_12.png",
                    "/assets/imgs/case-studies/ipm_13.png",
                    "/assets/imgs/case-studies/ipm_14.png",
                    "/assets/imgs/case-studies/ipm_15.png",
                  ].map((id, index) => (
                    <motion.img
                      key={id}
                      src={id}
                      alt={`Iteration ${index + 1}`}
                      className="w-full rounded-lg"
                      initial={{ opacity: 0, scale: 0.95 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: index * 0.1 }}
                    />
                  ))}
                </div>
              </div>
            </AnimatedSection>
          </section>
        </ParallaxSection>

        {/* High Fidelity Wireframes */}
        <ParallaxSection speed={0.5}>
          <section className="py-12 md:py-16 px-4">
            <AnimatedSection>
              <div className="space-y-8">
                <h3 className="text-[24px] leading-[30px] md:text-h2 font-normal text-white">
                  High Fidelity Wireframes
                </h3>

                <div className="grid grid-cols-1 gap-8">
                  {[
                    "/assets/imgs/case-studies/ipm_8.png",
                    "/assets/imgs/case-studies/ipm_9.png",
                    "/assets/imgs/case-studies/ipm_10.png",
                  ].map((id, index) => (
                    <motion.img
                      key={id}
                      src={id}
                      alt={`High Fidelity Wireframe ${index + 1}`}
                      className="w-full rounded-lg"
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: index * 0.1 }}
                    />
                  ))}
                </div>
              </div>
            </AnimatedSection>
          </section>
        </ParallaxSection>

        {/* Usability Testing III */}
        <ParallaxSection speed={0.4}>
          <section className="py-12 md:py-16 px-4">
            <AnimatedSection>
              <div className="space-y-8">
                <h3 className="text-[24px] leading-[30px] md:text-h2 font-normal text-white">
                  High-Fidelity Wireframes & Usability Testing III
                </h3>

                <div className="space-y-6">
                  <p className="text-body-sm md:text-body-new">
                    For the third round (high-fidelity wireframes), I conducted
                    moderated remote tests with 4 participants on Microsoft
                    Teams. Sessions were 30–40 minutes each; participants used a
                    Figma prototype and completed the same task set used in
                    prior rounds:
                  </p>

                  <p className="text-body-sm md:text-body-new">
                    <span className="text-white font-bold">Tasks:</span>{" "}
                    onboarding, navigation, setting a price, dynamic pricing
                    simulation, customization, and reviewing insights.
                  </p>
                </div>

                <motion.img
                  src="/assets/imgs/case-studies/ipm_7.png"
                  alt="High Fidelity Testing"
                  className="w-full rounded-lg"
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                />
              </div>
            </AnimatedSection>
          </section>
        </ParallaxSection>

        {/* Key Findings Round 3 */}
        <ParallaxSection speed={0.5}>
          <section className="py-12 md:py-16 px-4">
            <AnimatedSection>
              <div className="space-y-8">
                <h3 className="text-[24px] leading-[30px] md:text-h2 font-normal text-white">
                  Key Findings (Round 3)
                </h3>

                <div className="space-y-4">
                  <GlassCard delay={0}>
                    <p className="text-body-new">
                      • 4 / 4 participants completed onboarding successfully but
                      were confused about the location access step.
                    </p>
                  </GlassCard>

                  <GlassCard delay={0.05}>
                    <p className="text-body-new">
                      • 3 / 5 participants (note: reported as such in feedback)
                      said labels on icons improved understanding and
                      navigation.
                    </p>
                  </GlassCard>

                  <GlassCard delay={0.1}>
                    <p className="text-body-new">
                      • Participants liked the pricing suggestions; dynamic
                      graphs and toggles were helpful for adjusting rates.
                    </p>
                  </GlassCard>

                  <GlassCard delay={0.15}>
                    <p className="text-body-new">
                      • 4 / 4 participants appreciated the cost management
                      features (pie chart, report sharing, expense tracking and
                      editing).
                    </p>
                  </GlassCard>

                  <GlassCard delay={0.2}>
                    <p className="text-body-new">
                      • 3 / 4 participants praised the clean, minimal design,
                      but suggested adding Airbnb brand colors to boost
                      identity.
                    </p>
                  </GlassCard>

                  <GlassCard delay={0.25}>
                    <p className="text-body-new">
                      • All 4 / 4 participants valued potential enhancements:
                      host community, integrated booking calendar, and more
                      pricing customization—while also appreciating existing
                      reporting and cost tools.
                    </p>
                  </GlassCard>
                </div>
              </div>
            </AnimatedSection>
          </section>
        </ParallaxSection>

        {/* Actionable Next Steps Round 3 */}
        <ParallaxSection speed={0.4}>
          <section className="py-12 md:py-16 px-4">
            <AnimatedSection>
              <CoralCard>
                <h3 className="text-[24px] leading-[30px] md:text-h2 font-normal text-white mb-8 !text-white">
                  Actionable Next Steps (informed by Round 3)
                </h3>

                <div className="space-y-3">
                  <div className="flex gap-3">
                    <span className="text-[#FD5B38] text-2xl font-normal">
                      1.
                    </span>
                    <p className="text-body-light">
                      Add a host community space, integrate a booking calendar,
                      and expand pricing customization tools for collaboration
                      and planning.
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <span className="text-[#FD5B38] text-2xl font-normal">
                      2.
                    </span>
                    <p className="text-body-light">
                      Introduce Airbnb-themed color accents and keep consistent
                      spacing/layout to improve visual familiarity.
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <span className="text-[#FD5B38] text-2xl font-normal">
                      3.
                    </span>
                    <p className="text-body-light">
                      Apply icon labels consistently across the app to reduce
                      confusion.
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <span className="text-[#FD5B38] text-2xl font-normal">
                      4.
                    </span>
                    <p className="text-body-light">
                      Allow toggling between manual and smart pricing, and add
                      an info icon explaining how smart pricing is generated.
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <span className="text-[#FD5B38] text-2xl font-normal">
                      5.
                    </span>
                    <p className="text-body-light">
                      Continue supporting users with visual tools (pie charts,
                      editable expenses) and enhance reporting for clearer
                      financial planning.
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <span className="text-[#FD5B38] text-2xl font-normal">
                      6.
                    </span>
                    <p className="text-body-light">
                      Use ZIP code–based property entry instead of immediate GPS
                      prompts; delay location access until property setup to
                      improve onboarding and privacy control.
                    </p>
                  </div>
                </div>
              </CoralCard>
            </AnimatedSection>
          </section>
        </ParallaxSection>

        {/* User Feedback */}
        <ParallaxSection speed={0.5}>
          <section className="py-12 md:py-16 px-4">
            <AnimatedSection>
              <div className="space-y-12">
                <h3 className="text-[24px] leading-[30px] md:text-h2 font-normal text-white">
                  User Feedback
                </h3>

                <GlassCard>
                  <h4 className="text-h4 font-normal text-[#FD5B38] mb-8">
                    What Users Liked
                  </h4>

                  <div className="space-y-6">
                    <div className="border-l-4 border-[#FD5B38] pl-7">
                      <h5 className="text-h4 !text-white mb-3">
                        1. Smooth & Structured Onboarding
                      </h5>
                      <p className="text-body-sm md:text-body text-[#D1D5DC] mb-2">
                        Onboarding was intuitive, step-by-step, and reduced
                        friction.
                      </p>
                      <div className="space-y-1">
                        <p className="text-body-sm md:text-body italic text-[#99A1AF]">
                          Quotes: "Everything was clear — pretty smooth."
                        </p>
                        <p className="text-body-sm md:text-body italic text-[#99A1AF]">
                          "The onboarding, pricing simulation, and review
                          insights were all clear."
                        </p>
                      </div>
                    </div>

                    <div className="border-l-4 border-[#FD5B38] pl-7">
                      <h5 className="text-h4 !text-white mb-3">
                        2. Improved Navigation with Icon Labels
                      </h5>
                      <p className="text-body-new">
                        Labels under icons made navigation faster and clearer,
                        especially for new users.
                      </p>
                    </div>

                    <div className="border-l-4 border-[#FD5B38] pl-7">
                      <h5 className="text-h4 !text-white mb-3">
                        3. Helpful Pricing Features & Real-Time Adjustments
                      </h5>
                      <p className="text-body-sm md:text-body text-[#D1D5DC] mb-2">
                        Dynamic toggles, editable price components, and
                        real-time market data increased confidence.
                      </p>
                      <p className="text-body-sm md:text-body italic text-[#99A1AF]">
                        Quotes: "The dynamic pricing toggle and the graph
                        showing real-time updates — that is good."
                      </p>
                    </div>

                    <div className="border-l-4 border-[#FD5B38] pl-7">
                      <h5 className="text-h4 !text-white mb-3">
                        4. Visual Cost Management & Reporting
                      </h5>
                      <p className="text-body-sm md:text-body text-[#D1D5DC] mb-2">
                        Pie charts and downloadable reports helped monitor
                        expenses and share performance.
                      </p>
                      <p className="text-body-sm md:text-body italic text-[#99A1AF]">
                        Quotes: "Operational cost input and seeing the pie chart
                        was smooth."
                      </p>
                    </div>

                    <div className="border-l-4 border-[#FD5B38] pl-7">
                      <h5 className="text-h4 !text-white mb-3">
                        5. Clean & Minimal Interface Design
                      </h5>
                      <p className="text-body-sm md:text-body text-[#D1D5DC] mb-2">
                        Users described the UI as modern, clean, and
                        focused—helpful for concentrating on tasks.
                      </p>
                      <p className="text-body-sm md:text-body italic text-[#99A1AF]">
                        Quotes: "Design is pretty modern and clean… I appreciate
                        the minimal design."
                      </p>
                    </div>
                  </div>
                </GlassCard>

                <GlassCard>
                  <h4 className="text-h4 font-normal text-[#FD5B38] mb-8">
                    What Users Suggested
                  </h4>

                  <div className="space-y-6">
                    <div className="border-l-4 border-[#FD5B38] pl-7">
                      <h5 className="text-h4 !text-white mb-3">
                        1. Clarify Location Access Purpose
                      </h5>
                      <p className="text-body-sm md:text-body text-[#D1D5DC] mb-2">
                        Users were unsure why the app requested location during
                        onboarding; suggested allowing ZIP code entry and
                        deferring GPS prompts.
                      </p>
                      <p className="text-body-sm md:text-body italic text-[#99A1AF]">
                        Quotes: "When we entered the first flow after
                        onboarding, I was asked for this location — but for
                        what?"
                      </p>
                    </div>

                    <div className="border-l-4 border-[#FD5B38] pl-7">
                      <h5 className="text-h4 !text-white mb-3">
                        2. Consistent Icon Labels
                      </h5>
                      <p className="text-body-new">
                        Request for uniform labeling across all screens to
                        reduce ambiguity.
                      </p>
                    </div>

                    <div className="border-l-4 border-[#FD5B38] pl-7">
                      <h5 className="text-h4 !text-white mb-3">
                        3. Improve Visual Branding for Familiarity
                      </h5>
                      <p className="text-body-new">
                        Some recommended aligning UI accents with Airbnb's
                        design language (e.g., coral red) to build trust.
                      </p>
                    </div>

                    <div className="border-l-4 border-[#FD5B38] pl-7">
                      <h5 className="text-h4 !text-white mb-3">
                        4. Add Community Features
                      </h5>
                      <p className="text-body-new">
                        Interest in a host community hub to share tips, pricing
                        strategies, and local insights.
                      </p>
                    </div>
                  </div>
                </GlassCard>
              </div>
            </AnimatedSection>
          </section>
        </ParallaxSection>

        {/* Future Considerations */}
        <ParallaxSection speed={0.4}>
          <section className="py-12 md:py-16 px-4">
            <AnimatedSection>
              <GlassCard>
                <h3 className="text-[24px] leading-[30px] md:text-h2 font-normal text-white mb-8">
                  Future Considerations
                </h3>

                <p className="text-body-sm md:text-body text-[#D1D5DC] mb-8">
                  Planned enhancements to strengthen trust, onboarding
                  flexibility, brand familiarity, and community engagement:
                </p>

                <div className="space-y-4">
                  <div className="border-l-4 border-[#FD5B38] pl-7">
                    <h4 className="text-h4 font-normal text-[#FD5B38] mb-2">
                      • Host Community Space:
                    </h4>
                    <p className="text-body-new">
                      a regional or property-type organized hub for
                      collaboration, tips, and peer learning.
                    </p>
                  </div>

                  <div className="border-l-4 border-[#FD5B38] pl-7">
                    <h4 className="text-h4 font-normal text-[#FD5B38] mb-2">
                      • Airbnb-Themed Branding Cues:
                    </h4>
                    <p className="text-body-new">
                      subtle visual ties to Airbnb's design language to reduce
                      cognitive load between platforms.
                    </p>
                  </div>

                  <div className="border-l-4 border-[#FD5B38] pl-7">
                    <h4 className="text-h4 font-normal text-[#FD5B38] mb-2">
                      • Standardize Icon Labeling:
                    </h4>
                    <p className="text-body-new">
                      consistent descriptive labels + tooltips on hover/tap.
                    </p>
                  </div>

                  <div className="border-l-4 border-[#FD5B38] pl-7">
                    <h4 className="text-h4 font-normal text-[#FD5B38] mb-2">
                      • Manual ZIP Code Entry:
                    </h4>
                    <p className="text-body-new">
                      allow ZIP code input for property setup; defer GPS
                      permission requests until needed.
                    </p>
                  </div>
                </div>
              </GlassCard>
            </AnimatedSection>
          </section>
        </ParallaxSection>

        {/* Future Scope */}
        <ParallaxSection speed={0.5}>
          <section className="py-12 md:py-16 px-4">
            <AnimatedSection>
              <CoralCard>
                <h3 className="text-[24px] leading-[30px] md:text-h2 font-normal text-white mb-8 !text-white">
                  Future Scope
                </h3>

                <p className="text-body-sm md:text-body text-[#E5E7EB] mb-8">
                  Longer-term development priorities:
                </p>

                <div className="space-y-4">
                  <div className="bg-black/20 rounded-2xl p-6">
                    <h4 className="text-h4 font-normal text-[#FD5B38] mb-2">
                      1. Automated Price Push:
                    </h4>
                    <p className="text-body-light">
                      direct API integration with Airbnb to push accepted
                      recommended prices automatically.
                    </p>
                  </div>

                  <div className="bg-black/20 rounded-2xl p-6">
                    <h4 className="text-h4 font-normal text-[#FD5B38] mb-2">
                      2. Multi-Channel Support:
                    </h4>
                    <p className="text-body-light">
                      extend pricing recommendations across platforms
                      (Booking.com, Vrbo, etc.).
                    </p>
                  </div>

                  <div className="bg-black/20 rounded-2xl p-6">
                    <h4 className="text-h4 font-normal text-[#FD5B38] mb-2">
                      3. Model Explainability Enhancement:
                    </h4>
                    <p className="text-body-light">
                      evolve from static importance charts to interactive,
                      conversational AI explanations for pricing
                      recommendations.
                    </p>
                  </div>
                </div>
              </CoralCard>
            </AnimatedSection>
          </section>
        </ParallaxSection>

        {/* References Section */}
        <ParallaxSection speed={0.3}>
          <section className="bg-gradient-dark py-16 md:py-24 px-4">
            <div className="mx-auto max-w-7xl">
              <AnimatedSection>
                <div className="space-y-16">
                  <div className="flex items-center gap-3">
                    <div className="h-1 w-12 bg-[#FD5B38] rounded-full" />
                    <h2 className="text-[32px] leading-[38px] md:text-h1 font-normal text-white">
                      References
                    </h2>
                  </div>

                  <div className="space-y-6">
                    <GlassCard delay={0}>
                      <div className="flex gap-4">
                        <span className="bullet-coral" />
                        <div>
                          <p className="text-white text-body-sm">
                            Ahuja, A., Lahiri, A., & Das, A.{" "}
                            <span className="text-[#99A1AF]">(2021)</span>
                          </p>
                          <p className="text-body-sm text-[#D1D5DC] italic">
                            Predicting Airbnb Rental Prices Using Multiple
                            Feature Modalities.{" "}
                            <span className="text-[#99A1AF] not-italic">
                              arXiv.
                            </span>
                          </p>
                        </div>
                      </div>
                    </GlassCard>

                    <GlassCard delay={0.05}>
                      <div className="flex gap-4">
                        <span className="bullet-coral" />
                        <div>
                          <p className="text-white text-body-sm">
                            De Lima, L. C., & Da Silva, F. C.{" "}
                            <span className="text-[#99A1AF]">(2021)</span>
                          </p>
                          <p className="text-body-sm text-[#D1D5DC] italic">
                            Predicting Airbnb Listing Prices with Machine
                            Learning Models: A Case Study in Lisbon.{" "}
                            <span className="text-[#99A1AF] not-italic">
                              Journal of Data Science and Applications.
                            </span>
                          </p>
                        </div>
                      </div>
                    </GlassCard>

                    <GlassCard delay={0.1}>
                      <div className="flex gap-4">
                        <span className="bullet-coral" />
                        <div>
                          <p className="text-white text-body-sm">
                            Jiang, L., Li, Y., Luo, N., & Wang, J.{" "}
                            <span className="text-[#99A1AF]">(2022)</span>
                          </p>
                          <p className="text-body-sm text-[#D1D5DC] italic">
                            A Multi-Source Information Learning Framework for
                            Airbnb Price Prediction.{" "}
                            <span className="text-[#99A1AF] not-italic">
                              arXiv.
                            </span>
                          </p>
                        </div>
                      </div>
                    </GlassCard>

                    <GlassCard delay={0.15}>
                      <div className="flex gap-4">
                        <span className="bullet-coral" />
                        <div>
                          <p className="text-white text-body-sm">
                            Kalehbasti, P. R., Nikolenko, L., & Rezaei, H.{" "}
                            <span className="text-[#99A1AF]">(2019)</span>
                          </p>
                          <p className="text-body-sm text-[#D1D5DC] italic">
                            Airbnb Price Prediction Using Machine Learning and
                            Sentiment Analysis.{" "}
                            <span className="text-[#99A1AF] not-italic">
                              arXiv.
                            </span>
                          </p>
                        </div>
                      </div>
                    </GlassCard>

                    <GlassCard delay={0.2}>
                      <div className="flex gap-4">
                        <span className="bullet-coral" />
                        <div>
                          <p className="text-white text-body-sm">
                            Perez-Sanchez, J., Serrano-Estrada, L., & Marti, P.{" "}
                            <span className="text-[#99A1AF]">(2021)</span>
                          </p>
                          <p className="text-body-sm text-[#D1D5DC] italic">
                            Factors Influencing Airbnb Prices: A Study of
                            Listings in Urban Areas.{" "}
                            <span className="text-[#99A1AF] not-italic">
                              Springer.
                            </span>
                          </p>
                        </div>
                      </div>
                    </GlassCard>

                    <GlassCard delay={0.25}>
                      <div className="flex gap-4">
                        <span className="bullet-coral" />
                        <div>
                          <p className="text-white text-body-sm">
                            Wong, F. F., & Yu, S. Z.{" "}
                            <span className="text-[#99A1AF]">(2020)</span>
                          </p>
                          <p className="text-body-sm text-[#D1D5DC] italic">
                            Using Machine Learning to Predict Airbnb Price and
                            Demand.{" "}
                            <span className="text-[#99A1AF] not-italic">
                              International Journal of Hospitality Management.
                            </span>
                          </p>
                        </div>
                      </div>
                    </GlassCard>
                  </div>
                </div>
              </AnimatedSection>
            </div>
          </section>
        </ParallaxSection>
      </div>
    </div>
  );
}

export default IntelligentPricingModelPage;
